import axios from "axios";
import Vue from "vue";
import Vuex from "vuex";
import createPersistedState from "vuex-persistedstate";
import quizModule from "./quiz";

import { AjaxReturnViewModel } from "@/interfaces/AjaxReturnViewModel";
import { CheckoutViewModel } from "@/interfaces/CheckoutViewModel";
import { CustomerDetailViewModel } from "@/interfaces/CustomerDetailViewModel";
import { OrderViewModel } from "@/interfaces/OrderViewModel";
import { OTCCartCheckoutViewModel } from "@/interfaces/OTCCartCheckoutViewModel";
import { OTCProductViewModel } from "@/interfaces/OTCProductViewModel";
import { TwoFactorTypeEnum } from "@/interfaces/TwoFactorTypeEnum";
import { Patient } from "@/interfaces/Patient";
import { PatientNote } from "@/interfaces/PatientNote";
import { PatientPortalViewModel } from "@/interfaces/PatientPortalViewModel";
import { PatientRegisterViewModel } from "@/interfaces/PatientRegisterViewModel";
import { PatientViewModel } from "@/interfaces/PatientViewModel";
import { PaymentViewModel } from "@/interfaces/PaymentViewModel";
import { PharmacySchedule } from "@/interfaces/PharmacySchedule";
import { PhotosUpdateViewModel } from "@/interfaces/PhotosUpdateViewModel";
import { PhotosViewModel } from "@/interfaces/PhotosViewModel";
import { PhotoVerifyViewModel } from "@/interfaces/PhotoVerifyViewModel";
import { PhysicianReviewDataViewModel } from "@/interfaces/PhysicianReviewViewModel";
import { QuestionnaireTypeProductViewModel } from "@/interfaces/QuestionnaireTypeProductViewModel";
import { QuestionnaireViewModel } from "@/interfaces/QuestionnaireViewModel";
import { Questions } from "@/interfaces/Questions";
import { QuestViewModel } from "@/interfaces/QuestViewModel";
import { RefillViewModel } from "@/interfaces/RefillViewModel";
import { Rx } from "@/interfaces/Rx";
import { RxPaymentViewModel } from "@/interfaces/RxPaymentViewModel";
import { RxRefillViewModel } from "@/interfaces/RxRefillViewModel";
import { ShoppingCartItem } from "@/interfaces/ShoppingCartItem";
import { ShoppingCartViewModel } from "@/interfaces/ShoppingCartViewModel";
import { VisitScheduleViewModel } from "@/interfaces/VisitScheduleViewModel";
import router from "@/router";
import api from "@/webapi";
import { SubscriptionPlanViewModel } from "@/interfaces/SubscriptionPlanViewModel";
import { AccountSettingsViewModel } from "@/interfaces/AccountSettingsViewModel";
import { PharmacySpecialHours } from "@/interfaces/PharmacySpecialHours";
import { AdminUserViewModel } from "@/interfaces/AdminUserViewModel";
import { QuestionnaireProductRxsDetails } from "@/interfaces/QuestionnaireProductRxsDetails";
import { NewPromotionViewModel } from "@/interfaces/NewPromotionViewModel";
import { PromotionRx } from "@/interfaces/PromotionEditViewModel";
import { ReportDataManagementViewModel, ReportDataViewModel } from "@/interfaces/ReportDataViewModel";
import { AdminEmailUpdateViewModel } from "@/interfaces/AdminEmailUpdateViewModel";
import { LookupPatientAsPhysicianViewModel } from "@/interfaces/LookupPatientAsPhysicianViewModel";
import { PatientPortalRxContainer } from "@/interfaces/PatientPortalRxContainer";
import { LookupPatientAsSupportViewModel } from "@/interfaces/LookupPatientAsSupportViewModel";
import { OutgoingReport } from "@/interfaces/OutgoingReport";
import { UsersForAdminViewModel } from "@/interfaces/UsersForAdminViewModel";
import { ReportTableData } from "@/interfaces/ReportTableDataViewModel";
import { PatientVitalViewModel } from "@/interfaces/PatientVitalViewModel";
import { SupportedState } from "@/interfaces/SupportedState";
import { VisitType } from "@/interfaces/VisitType";
import { MedicationMarketplaceDrugListViewModel } from "@/interfaces/MedicationMarketplaceDrugListViewModel";
import { MarketplaceEstimatesViewModel } from "@/interfaces/MarketplaceEstimatesViewModel";
import { PatientPortalMedicationMarketplaceRx } from "@/interfaces/PatientPortalMedicationMarketplaceRx";
import { AffiliateSubscription } from "@/interfaces/AffiliateSubscription";
import { PatientAffiliateSubscriptionUpdate } from "@/interfaces/PatientAffiliateSubscriptionUpdate";
import { SubscriptionTransferViewModel } from "@/interfaces/SubscriptionTransferViewModel";
import { FamilyPlanManagerViewModel } from "@/interfaces/FamilyPlanManagerViewModel";
import { PatientMedicalRecordViewModel } from "@/interfaces/PatientMedicalRecordViewModel";
import { Permission } from "@/interfaces/Permission";
import { Role } from "@/interfaces/Roles";
import { PermissionGrantedViewModel } from "@/interfaces/PermissionGrantedViewModel";
import { UpdateAffiliateSubscriptionsViewModel } from "@/interfaces/UpdateAffiliateSubscriptionsViewModel";
import { MedicationMarketplaceSubscriptionManagementViewModel } from "@/interfaces/MedicationMarketplaceSubscriptionManagementViewModel";
import { MarketplaceRxPaymentViewModel } from "@/interfaces/MarketplaceRxPaymentViewModel";
import { PatientAffiliateSubscription } from "@/interfaces/PatientAffiliateSubscription";

Vue.use(Vuex);

export default new Vuex.Store({
  state: {
    MaintenanceDetails: {},
    CallToActionArray: [],
    SiteSetupTimeStamp: null as Date | null,
    Affiliate: {},
    AffiliateFeatures: [],
    Role: "",
    Token: "",
    RefreshToken: "",
    Lang: "EN",
    LastRefreshDate: new Date(),
    SignedIn: false,
    Content: {
      SiteTitle: "",
      HomeHeroText: "Create your own hero section here",
      HomeHeroSubText: "Everything is customizable from the image to the text and color. Use this section to show the benefits of your offering.",
      ShippingPaymentInfoTitle: "Where are we sending your medication?",
      ShippingPaymentInfoSubTitle: "All medicines are shipped in discreet packaging, keeping in mind your privacy at all times.",
      SiteHomepage: "",
      MaintenanceModeContent: "",
    },
    Theme: null as Record<string, unknown> | null,
    QuestionnaireTypeProducts: [] as QuestionnaireTypeProductViewModel[],
    OTCProducts: [] as OTCProductViewModel[],
    GrantedPermissions: [] as PermissionGrantedViewModel[],
    Roles: [] as Role[],
    Cart: {},
    ShoppingCart: {} as ShoppingCartViewModel,
    ShowCart: false,
    Loading: false,
    BypassAppLoading: false,
    Questionnaires: {} as Record<number, QuestViewModel>,
    Payments: {} as Record<number, PaymentViewModel>,
    Refills: {} as Record<number, RefillViewModel>,
    Photos: {} as Record<number, PhotosUpdateViewModel>,
    RxPayments: {} as Record<number, RxPaymentViewModel>,
    RxRefills: {} as Record<number, RxRefillViewModel>,
    SubscriptionPlans: {} as Record<number, SubscriptionPlanViewModel>,
    CurrentQuestType: null as number | null,
    CurrentRxID: null as number | null,
    CurrentSubscriptionProgramID: null as number | null,
    CurrentRefillNum: null as number | null,
    CurrentAddRxPatient: null as Patient | null,
    IsCollectJSLoaded: false as boolean,
    PhotoPath: null as string | null,
    FirstName: null as string | null,
    LastName: null as string | null,
    Email: null as string | null,
    ShippingCarriers: [],
    UTM: null,
    PassedPromoCode: null,
    showCart: false as boolean,
    ShowSidebarAdmin: false as boolean,
    CustomerLookup: [] as LookupPatientAsSupportViewModel[],
    ShowMainMenu: false as boolean,
    ReportData: {} as ReportTableData,
    PreviousScreen: "",
    ScheduledMaintenance: "",
    ScheduledMaintenanceDuration: 0,
    ScheduledMaintenanceDateTime: "",
    PreviousMaintenanceResult: false as boolean,
    ExpectedMaintenanceStart: "",
    ExpectedMaintenanceBool: false,
    MaintenanceAffiliateArray: [],
    DefaultMaintenanceHTML: "",
    NotWithinMaintenanceWindow: 3600000,
    WithinMaintenanceWindow: 900000,
    FullPatientWidgetData: null,
    IsVerifiedQuickLink: false,
    quickLinkHasBeenRedirected: false,
    IsQuickLinkUser: false,
    IsQuickLinkSupportUser: false,
    AutoSavePatientProfile: false,
    QuickLinkExternalID: null,
    QuickLinkSupportUserName: "",
    MarketplaceEstimates: [] as MarketplaceEstimatesViewModel[],
    ShowPharmacyCart: false as boolean,
    ShowSubscriptionManagement: false as boolean,
    PharmacyCartType: "", // This holds "rxReceived" or "rxEstimate"
    PharmacyCartView: 0, // This holds enum for desired view
    ForcePharmacyCartFlow: false as boolean,
    ShowSubscriptionPaymentFlow: false as boolean,
    SubscriptionPaymentFlowView: 0,
    ForceSubscriptionFlow: false as boolean,
    MarketplaceRxs: [] as PatientPortalMedicationMarketplaceRx[],
    PatientAffiliateSubscriptionUpdate: {} as PatientAffiliateSubscriptionUpdate,
    TotalUnreadSupportMessages: 0 as number,
    PatientAffiliateSubscription: {} as PatientAffiliateSubscription,
  },
  getters: {
    getQuickLinkSupportUserName: (state) => state.QuickLinkSupportUserName,
    getQuickLinkExternalID: (state) => state.QuickLinkExternalID,
    getIsQuickLinkSupportUser: (state) => state.IsQuickLinkSupportUser,
    getQuickLinkHasBeenRedirected: (state) => state.quickLinkHasBeenRedirected,
    getFullPatientWidgetData: (state) => state.FullPatientWidgetData,
    getNotWithinMaintenanceWindow: (state) => state.NotWithinMaintenanceWindow,
    getWithinMaintenanceWindow: (state) => state.WithinMaintenanceWindow,
    getScheduledMaintenanceDuration: (state) => state.ScheduledMaintenanceDuration,
    getScheduledMaintenanceDateTime: (state) => state.ScheduledMaintenanceDateTime,
    getDefaultMaintenanceHTML: (state) => state.DefaultMaintenanceHTML,
    getMaintenanceAffiliateArray: (state) => state.MaintenanceAffiliateArray,
    getExpectedMaintenanceStart: (state) => state.ExpectedMaintenanceStart,
    getExpectedMaintenanceBool: (state) => state.ExpectedMaintenanceBool,
    getPreviousScreen: (state) => state.PreviousScreen,
    getPreviousMaintenanceResult: (state) => state.PreviousMaintenanceResult,
    getMaintenanceDetails: (state) => state.MaintenanceDetails,
    getCallToActionArray: (state) => state.CallToActionArray,
    getScheduledMaintenance: (state) => state.ScheduledMaintenance,
    getAffiliate: (state) => state.Affiliate,
    getAffiliateFeatures: (state) => state.AffiliateFeatures,
    getSignedIn: (state) => state.SignedIn,
    getTheme: (state) => state.Theme,
    getContent: (state) => state.Content,
    getLoading: (state) => state.Loading,
    getToken: (state) => state.Token,
    getRefreshToken: (state) => state.RefreshToken,
    getQuestionnaires: (state) => state.Questionnaires,
    getPayments: (state) => state.Payments,
    getRefills: (state) => state.Refills,
    getPhotos: (state) => state.Photos,
    getRxPayments: (state) => state.RxPayments,
    getRxRefills: (state) => state.RxRefills,
    getSubscriptionPlans: (state) => state.SubscriptionPlans,
    getShoppingCart: (state) => state.ShoppingCart,
    getCurrentQuestType: (state) => state.CurrentQuestType,
    getCurrentRefillNum: (state) => state.CurrentRefillNum,
    getIsCollectJSLoaded: (state) => state.IsCollectJSLoaded,
    getRole: (state) => state.Role,
    getLang: (state) => state.Lang,
    getPhotoPath: (state) => state.PhotoPath,
    getFirstName: (state) => state.FirstName,
    getLastName: (state) => state.LastName,
    getFullName: (state) => `${state.FirstName} ${state.LastName}`,
    getEmail: (state) => state.Email,
    getQuestionnaireTypeProducts: (state) => state.QuestionnaireTypeProducts,
    getOTCProducts: (state) => state.OTCProducts,
    getGrantedPermissions: (state) => state.GrantedPermissions,
    getRoles: (state) => state.Roles,
    getLastRefreshDate: (state) => state.LastRefreshDate,
    getCurrentAddRxPatient: (state) => state.CurrentAddRxPatient,
    getCurrentRxID: (state) => state.CurrentRxID,
    getCurrentSubscriptionProgramID: (state) => state.CurrentSubscriptionProgramID,
    getShippingCarriers: (state) => state.ShippingCarriers,
    getUTM: (state) => state.UTM,
    getShowCart: (state) => state.ShowCart,
    getShowPharmacyCart: (state) => state.ShowPharmacyCart,
    getShowSubscriptionManagement: (state) => state.ShowSubscriptionManagement,
    getShowSidebarAdmin: (state) => state.ShowSidebarAdmin,
    getSiteSetupTimeStamp: (state) => state.SiteSetupTimeStamp,
    getPassedPromoCode: (state) => state.PassedPromoCode,
    getCustomerLookup: (state) => state.CustomerLookup,
    getShowMainMenu: (state) => state.ShowMainMenu,
    getReportData: (state) => state.ReportData,
    getIsVerifiedQuickLink: (state) => state.IsVerifiedQuickLink,
    getAutoSavePatientProfile: (state) => state.AutoSavePatientProfile,
    getIsQuickLinkUser: (state) => state.IsQuickLinkUser,
    getMarketplaceEstimates: (state) => state.MarketplaceEstimates,
    getPharmacyCartType: (state) => state.PharmacyCartType,
    getPharmacyCartView: (state) => state.PharmacyCartView,
    getForcePharmacyCartFlow: (state) => state.ForcePharmacyCartFlow,
    getShowSubscriptionPaymentFlow: (state) => state.ShowSubscriptionPaymentFlow,
    getSubscriptionPaymentFlowView: (state) => state.SubscriptionPaymentFlowView,
    getForceSubscriptionFlow: (state) => state.ForceSubscriptionFlow,
    getMarketplaceRxs: (state) => state.MarketplaceRxs,
    getPatientAffiliateSubscriptionUpdate: (state) => state.PatientAffiliateSubscriptionUpdate,
    getTotalUnreadSupportMessages: (state) => state.TotalUnreadSupportMessages,
    getPatientAffiliateSubscription: (state) => state.PatientAffiliateSubscription,
  },
  mutations: {
    initialiseStore(state) {
      // Check if the ID exists
      state.IsCollectJSLoaded = false;
      if (sessionStorage.getItem("s")) {
        const savedStore = sessionStorage.getItem("s");
        // Replace the state object with the stored item
        this.replaceState(Object.assign(state, JSON.parse(savedStore ? savedStore : "")));
        state.IsCollectJSLoaded = false;
      }
    },
    setTotalUnreadSupportMessages(state, count) {
      state.TotalUnreadSupportMessages = count;
    },
    setQuickLinkSupportUserName(state, email) {
      state.QuickLinkSupportUserName = email;
    },
    setQuickLinkExternalID(state, guid) {
      state.QuickLinkExternalID = guid;
    },
    setIsQuickLinkSupportUser(state, bool) {
      state.IsQuickLinkSupportUser = bool;
    },
    setIsQuickLinkUser(state, bool) {
      state.IsQuickLinkUser = bool;
    },
    setQuickLinkHasBeenRedirected(state, bool) {
      state.quickLinkHasBeenRedirected = bool;
    },
    setIsVerifiedQuickLink(state, IsVerifiedQuickLink) {
      state.IsVerifiedQuickLink = IsVerifiedQuickLink;
    },
    setFullPatientWidgetData(state, FullPatientWidgetData) {
      state.FullPatientWidgetData = FullPatientWidgetData;
    },
    setNotWithinMaintenanceWindow(state, NotWithinMaintenanceWindow) {
      state.NotWithinMaintenanceWindow = NotWithinMaintenanceWindow;
    },
    setWithinMaintenanceWindow(state, WithinMaintenanceWindow) {
      state.WithinMaintenanceWindow = WithinMaintenanceWindow;
    },
    setScheduledMaintenanceDuration(state, ScheduledMaintenanceDuration) {
      state.ScheduledMaintenanceDuration = ScheduledMaintenanceDuration;
    },
    setScheduledMaintenanceDateTime(state, ScheduledMaintenanceDateTime) {
      state.ScheduledMaintenanceDateTime = ScheduledMaintenanceDateTime;
    },
    setLocalMaintenanceModeContent(state, NewContent) {
      state.Content.MaintenanceModeContent = NewContent;
    },
    setDefaultMaintenanceHTML(state, DefaultMaintenanceHTML) {
      state.DefaultMaintenanceHTML = DefaultMaintenanceHTML;
    },
    setMaintenanceAffiliateArray(state, MaintenanceAffiliateArray) {
      state.MaintenanceAffiliateArray = MaintenanceAffiliateArray;
    },
    setExpectedMaintenanceBool(state, ExpectedMaintenanceBool) {
      state.ExpectedMaintenanceBool = ExpectedMaintenanceBool;
    },
    setExpectedMaintenanceStart(state, ExpectedMaintenanceStart) {
      state.ExpectedMaintenanceStart = ExpectedMaintenanceStart;
    },
    setScheduledMaintenance(state, ScheduledMaintenance) {
      state.ScheduledMaintenance = ScheduledMaintenance;
    },
    setPreviousScreen(state, PreviousScreen) {
      state.PreviousScreen = PreviousScreen;
    },
    setPreviousMaintenanceResult(state, PreviousMaintenanceResult) {
      state.PreviousMaintenanceResult = PreviousMaintenanceResult;
    },
    setMaintenanceDetails(state, MaintenanceDetails) {
      state.MaintenanceDetails = MaintenanceDetails;
    },
    setCallToActionArray(state, callToActionArray) {
      state.CallToActionArray = callToActionArray;
    },
    setAffiliate(state, Affiliate) {
      state.Affiliate = Affiliate;
    },
    setAffiliateFeatures(state, AffiliateFeatures) {
      state.AffiliateFeatures = AffiliateFeatures;
    },
    setCurrentAddRxPatient(state, CurrentAddRxPatient) {
      state.CurrentAddRxPatient = CurrentAddRxPatient;
    },
    setContent(state, Content) {
      state.Content = Content;
    },
    setTheme(state, Theme) {
      state.Theme = Theme;
    },
    setQuestionnaireTypeProducts(state, QuestionnaireTypeProducts) {
      state.QuestionnaireTypeProducts = QuestionnaireTypeProducts;
    },
    setOTCProducts(state, OTCProducts) {
      state.OTCProducts = OTCProducts;
    },
    setGrantedPermissions(state, GrantedPermissions) {
      state.GrantedPermissions = GrantedPermissions;
    },
    setRoles(state, Roles) {
      state.Roles = Roles;
    },
    setLoading(state, Loading) {
      if (!state.BypassAppLoading) {
        state.Loading = Loading;
      }
    },
    setBypassAppLoading(state, Loading) {
      state.BypassAppLoading = Loading;
    },
    setToken(state, Token) {
      state.Token = Token;
      api.defaults.headers.common["Authorization"] = `Bearer ${Token}`;
      if (Token && Token.length > 0) state.SignedIn = true;
      else state.SignedIn = false;
    },
    setRefreshToken(state, RefreshToken) {
      state.RefreshToken = RefreshToken;
      state.LastRefreshDate = new Date();
    },
    setQuestionnaire(state, payload: { q: QuestViewModel; t: number }) {
      const q = Object.assign({}, state.Questionnaires);
      q[payload.t] = payload.q;
      state.Questionnaires = q;
    },
    setQuestionnaires(state, questionnaires) {
      state.Questionnaires = questionnaires;
    },
    setPayment(state, payload: { p: PaymentViewModel; t: number }) {
      const p = Object.assign({}, state.Payments);
      p[payload.t] = payload.p;
      state.Payments = p;
    },
    setRxPayment(state, payload: { p: RxPaymentViewModel; rxID: number }) {
      const p = Object.assign({}, state.RxPayments);
      p[payload.rxID] = payload.p;
      state.RxPayments = p;
    },
    setRxRefill(state, payload: { r: RxRefillViewModel; rxID: number }) {
      const r = Object.assign({}, state.RxRefills);
      r[payload.rxID] = payload.r;
      state.RxRefills = r;
    },
    setSubscriptionPlan(state, payload: { s: SubscriptionPlanViewModel; subscriptionProgramID: number }) {
      const s = Object.assign({}, state.SubscriptionPlans);
      s[payload.subscriptionProgramID] = payload.s;
      state.SubscriptionPlans = s;
    },
    setPhotos(state, payload: { p: PhotosUpdateViewModel; t: number }) {
      const p = Object.assign({}, state.Photos);
      p[payload.t] = payload.p;
      state.Photos = p;
    },
    setPaymentQuest(state, payload: { q: QuestionnaireViewModel; t: number }) {
      const p = Object.assign({}, state.Payments);
      p[payload.t].quest = payload.q;
      state.Payments = p;
    },
    setPaymentPatient(state, payload: { p: PatientViewModel; q: QuestionnaireViewModel | null; t: number }) {
      const p = Object.assign({}, state.Payments);
      p[payload.t].patient = payload.p;
      p[payload.t].quest = payload.q ? payload.q : p[payload.t].quest;
      state.Payments = p;
    },
    setRefill(state, payload: { r: RefillViewModel; t: number }) {
      const r = Object.assign({}, state.Refills);
      r[payload.t] = payload.r;
      state.Refills = r;
    },
    setRefillQuest(state, payload: { q: QuestionnaireViewModel; t: number }) {
      const r = Object.assign({}, state.Refills);
      r[payload.t].quest = payload.q;
      state.Refills = r;
    },
    setShoppingCart(state, cart) {
      state.ShoppingCart = cart;
    },
    setCurrentQuestType(state, CurrentQuestType) {
      state.CurrentQuestType = CurrentQuestType;
    },
    setCurrentRefillNum(state, CurrentRefillNum) {
      state.CurrentRefillNum = CurrentRefillNum;
    },
    setIsCollectJSLoaded(state, IsCollectJSLoaded) {
      state.IsCollectJSLoaded = IsCollectJSLoaded;
    },
    setRole(state, Role) {
      state.Role = Role;
    },
    setLang(state, Lang) {
      state.Lang = Lang;
    },
    setPhotoPath(state, PhotoPath) {
      state.PhotoPath = PhotoPath;
    },
    setFirstName(state, FirstName) {
      state.FirstName = FirstName;
    },
    setLastName(state, LastName) {
      state.LastName = LastName;
    },
    setEmail(state, Email) {
      state.Email = Email;
    },
    setCurrentRxID(state, CurrentRxID) {
      state.CurrentRxID = CurrentRxID;
    },
    setCurrentSubscriptionProgramID(state, CurrentSubscriptionProgramID) {
      state.CurrentSubscriptionProgramID = CurrentSubscriptionProgramID;
    },
    setShippingCarriers(state, ShippingCarriers) {
      state.ShippingCarriers = ShippingCarriers;
    },
    setUTM(state, UTM) {
      state.UTM = UTM;
    },
    setShowCart(state, ShowCart) {
      state.ShowCart = ShowCart;
    },
    setSidebarAdmin(state, ShowSidebarAdmin) {
      state.ShowSidebarAdmin = ShowSidebarAdmin;
    },
    setSiteSetupTimeStamp(state, SiteSetupTimeStamp) {
      state.SiteSetupTimeStamp = SiteSetupTimeStamp;
    },
    setPassedPromoCode(state, PassedPromoCode) {
      state.PassedPromoCode = PassedPromoCode;
    },
    setCustomerLookup(state, CustomerLookup) {
      state.CustomerLookup = CustomerLookup;
    },
    setShowMainMenu(state, ShowMainMenu) {
      state.ShowMainMenu = ShowMainMenu;
    },
    setReportTableData(state, newReportTableData) {
      state.ReportData = {
        ...state.ReportData,
        reportTableData: newReportTableData,
      };
    },
    setFilteredData(state, newFilteredData) {
      state.ReportData = {
        ...state.ReportData,
        filteredData: newFilteredData,
      };
    },
    setActiveFilters(state, newActiveFilters) {
      state.ReportData = {
        ...state.ReportData,
        activeFilters: newActiveFilters,
      };
    },
    setCurrentPage(state, newCurrentPage) {
      state.ReportData = {
        ...state.ReportData,
        currentPage: newCurrentPage,
      };
    },
    setPageSize(state, newPageSize) {
      state.ReportData = {
        ...state.ReportData,
        pageSize: newPageSize,
      };
    },
    setAutoSavePatientProfile(state, newAutoSavePatientProfile) {
      state.AutoSavePatientProfile = newAutoSavePatientProfile;
    },
    setMarketplaceEstimates(state, MarketplaceEstimates) {
      state.MarketplaceEstimates = MarketplaceEstimates;
    },

    setShowPharmacyCart(state, payload) {
      if ("show" in payload) {
        state.ShowPharmacyCart = payload.show;
      }
      if ("type" in payload) {
        state.PharmacyCartType = payload.type;
      }
      if ("view" in payload) {
        state.PharmacyCartView = payload.view;
      }
      if ("force" in payload) {
        state.ForcePharmacyCartFlow = payload.force;
      }

      // Examples:
      // this.setShowPharmacyCart({ show: true, type: "rxReceived", view: 1, force: false });
      // this.setShowPharmacyCart({ show: true, type: "rxEstimate", view: 2, force: true });
    },
    setShowSubscriptionManagement(state, value) {
      state.ShowSubscriptionManagement = value;
    },
    setShowSubscriptionPaymentFlow(state, payload) {
      if ("show" in payload) {
        state.ShowSubscriptionPaymentFlow = payload.show;
      }
      if ("view" in payload) {
        state.SubscriptionPaymentFlowView = payload.view;
      }
      if ("forceFlow" in payload) {
        state.ForceSubscriptionFlow = payload.forceFlow;
      }

      // Examples:
      // this.setShowSubscriptionPaymentFlow({ show: true, view: 1, forceFlow: false });
    },

    setMarketplaceRxs(state, rxs) {
      state.MarketplaceRxs = rxs;
    },
    setPatientAffiliateSubscriptionUpdate(state, payload) {
      state.PatientAffiliateSubscriptionUpdate = payload;
    },
    setPatientAffiliateSubscription(state, PatientAffiliateSubscription) {
      state.PatientAffiliateSubscription = PatientAffiliateSubscription;
    },
  },
  actions: {
    async initialiseStore({ commit, getters }) {
      commit("initialiseStore");
      commit("setToken", getters.getToken);
      commit("setRefreshToken", getters.getRefreshToken);
      commit("setLoading", false);
    },
    async SavePhotos({ commit, state }, payload: { p: PhotosViewModel; t: number; photosOnly: boolean }) {
      try {
        let q = {} as QuestViewModel | PhotosUpdateViewModel;
        if (payload.photosOnly) q = Object.assign({}, state.Photos[payload.t]);
        else q = Object.assign({}, state.Questionnaires[payload.t]);
        const response = await api.post(`/api/Home/SavePhotos?t=${payload.t}`, payload.p);
        if (response && response.data && response.data.success) {
          q.patient = response.data.data;
          if (payload.photosOnly) commit("setPhotos", { q: q, t: payload.t });
          else commit("setQuestionnaire", { q: q, t: payload.t });
          commit("setPhotoPath", q.patient.photo2Path);
          return true;
        }
        q.errors = response.data ? response.data.errors : [];
        q.navButtonText = response.data ? response.data.navButtonText : "";
        q.navPageID = response.data ? response.data.navPageID : null;
        if (payload.photosOnly) commit("setPhotos", { q: q, t: payload.t });
        else commit("setQuestionnaire", { q: q, t: payload.t });
        return false;
      } catch (e) {
        let q = {} as QuestViewModel | PhotosUpdateViewModel;
        if (payload.photosOnly) q = Object.assign({}, state.Photos[payload.t]);
        else q = Object.assign({}, state.Questionnaires[payload.t]);
        q.errors = ["Something went wrong!"];
        q.navButtonText = "Click to retry.";
        q.navPageID = null;
        if (payload.photosOnly) commit("setPhotos", { q: q, t: payload.t });
        else commit("setQuestionnaire", { q: q, t: payload.t });
        return false;
      }
    },
    async SaveQuestionsPhoto({ commit, state }, payload: { p: any }) {
      try {
        let rVM = { photoPath: "" };
        const response = await api.post(`/api/Questions/SavePhoto?`, payload.p);
        if (response && response.data && response.data.success) {
          rVM = response.data.data;
          return rVM.photoPath;
        }
        return null;
      } catch (e) {
        return null;
      }
    },
    async SaveQuestionsFile({ commit, state }, payload) {
      try {
        const response = await api.post("/api/questions/SaveFile", payload);
        if (response && response.data && response.data.success) {
          return response.data.data.filePath;
        }
        return null;
      } catch (e) {
        return null;
      }
    },
    async SavePatient({ commit, state }, payload: { p: PatientViewModel; quest?: QuestionnaireViewModel; t: number; isPayment?: boolean }) {
      try {
        const q = payload.isPayment ? Object.assign({}, state.Payments[payload.t]) : Object.assign({}, state.Questionnaires[payload.t]);
        const response = await api.post("/api/Home/SavePatient", payload.p);
        console.log("SavePatient Resolved!");
        if (response && response.data && response.data.success) {
          q.quest = payload.quest ? payload.quest : q.quest;
          q.patient = response.data.data;
          if (payload.isPayment) commit("setPayment", { p: q, t: payload.t });
          else commit("setQuestionnaire", { q: q, t: payload.t });
          commit("setPhotoPath", q.patient.photo2Path);
          return true;
        }
        q.errors = response.data ? response.data.errors : [];
        q.navButtonText = response.data ? response.data.navButtonText : "";
        q.navPageID = response.data ? response.data.navPageID : null;
        if (payload.isPayment) commit("setPayment", { p: q, t: payload.t });
        else commit("setQuestionnaire", { q: q, t: payload.t });
        return false;
      } catch (e) {
        const q = payload.isPayment ? Object.assign({}, state.Payments[payload.t]) : Object.assign({}, state.Questionnaires[payload.t]);
        q.errors = ["Something went wrong!"];
        q.navButtonText = "Click to retry.";
        q.navPageID = null;
        if (payload.isPayment) commit("setPayment", { p: q, t: payload.t });
        else commit("setQuestionnaire", { q: q, t: payload.t });
        return false;
      }
    },

    async SaveMarketplacePatient({ commit, state }, p: PatientViewModel) {
      try {
        const response = await api.post("/api/medicationmarketplace/SavePatient", p);
        if (response && response.data && response.data.success) {
          return true;
        }
      } catch (e) {
        console.error(e);
        return null;
      }
    },

    async OTCCartCheckout({ commit, state }, p: OTCCartCheckoutViewModel): Promise<AjaxReturnViewModel<boolean> | null> {
      try {
        const resp = await api.post("/api/Home/OTCCartCheckout", p);
        if (resp) {
          return resp.data;
        }
      } catch (e) {
        console.log(e);
      }
      return null;
    },
    async SaveShoppingCartItem({ commit, state }, payload: [ShoppingCartItem]) {
      try {
        const resp = await api.post("/api/Home/SaveShoppingCartItem", payload);
        if (resp) {
          return resp.data;
        }
      } catch (e) {
        console.log(e);
      }
    },
    async SaveShoppingCartItemForQuest({ commit, state }, payload: { items: [ShoppingCartItem]; t: number }) {
      try {
        const resp = await api.post(`/api/Home/SaveShoppingCartItemForQuest?t=${payload.t}`, payload.items);
        if (resp) {
          return resp.data;
        }
      } catch (e) {
        console.log(e);
      }
    },
    async SaveSubscriptionPlan({ commit, state }, payload: { s: SubscriptionPlanViewModel; l?: boolean }) {
      try {
        let s = Object.assign({}, state.SubscriptionPlans[payload.s.subPlan.subscriptionProgramID]);
        s.questionsObj = payload.s.questionsObj;
        const response = await api.post(`/api/Home/SaveSubscriptionPlan?isLastQuestPage=${payload.l ? "true" : "false"}`, payload.s);
        if (response && response.data && response.data.success) {
          s = response.data.data;
          s.errors = response.data ? response.data.errors : [];
          s.navButtonText = response.data ? response.data.navButtonText : "";
          s.navPageID = response.data ? response.data.navPageID : null;
          commit("setSubscriptionPlan", { s: s, subscriptionProgramID: payload.s.subPlan.subscriptionProgramID });
          return true;
        }
        s.errors = response.data ? response.data.errors : [];
        s.navButtonText = response.data ? response.data.navButtonText : "";
        s.navPageID = response.data ? response.data.navPageID : null;
        commit("setSubscriptionPlan", { s: s, subscriptionProgramID: payload.s.subPlan.subscriptionProgramID });
        return false;
      } catch (e) {
        /*const q = Object.assign({}, state.Questionnaires[payload.t]);
        q.errors = ["Something went wrong!"];
        q.navButtonText = "Click to retry.";
        q.navPageID = null;
        commit("setQuestionnaire", { q: q, t: payload.t });
        return false;*/
      }
    },
    async SaveQuest({ commit, state }, payload: { q: QuestViewModel; t: number; l?: boolean }) {
      try {
        const q = Object.assign({}, state.Questionnaires[payload.t]);
        const response = await api.post(`/api/Home/SaveQuest?isLastQuestPage=${payload.l ? "true" : "false"}`, payload.q);
        if (response && response.data && response.data.success) {
          q.quest = response.data.data;
          q.errors = response.data ? response.data.errors : [];
          q.navButtonText = response.data ? response.data.navButtonText : "";
          q.navPageID = response.data ? response.data.navPageID : null;
          commit("setQuestionnaire", { q: q, t: payload.t });
          return true;
        }
        q.errors = response.data ? response.data.errors : [];
        q.navButtonText = response.data ? response.data.navButtonText : "";
        q.navPageID = response.data ? response.data.navPageID : null;
        commit("setQuestionnaire", { q: q, t: payload.t });
        return false;
      } catch (e) {
        const q = Object.assign({}, state.Questionnaires[payload.t]);
        q.errors = ["Something went wrong!"];
        q.navButtonText = "Click to retry.";
        q.navPageID = null;
        commit("setQuestionnaire", { q: q, t: payload.t });
        return false;
      }
    },

    async SaveQuestAfterCheckout({ commit, state }, payload: { q: QuestViewModel; t: number; l?: boolean }) {
      try {
        const q = Object.assign({}, state.Questionnaires[payload.t]);
        const response = await api.post(`/api/Home/SaveQuestAfterCheckout?isLastQuestPage=${payload.l ? "true" : "false"}`, payload.q); // isLastQuestPage isn't being used, not sure if it's actually needed or not
        if (response && response.data && response.data.success) {
          q.quest = response.data.data;
          q.errors = response.data ? response.data.errors : [];
          q.navButtonText = response.data ? response.data.navButtonText : "";
          q.navPageID = response.data ? response.data.navPageID : null;
          commit("setQuestionnaire", { q: q, t: payload.t });
          return true;
        }
        q.errors = response.data ? response.data.errors : [];
        q.navButtonText = response.data ? response.data.navButtonText : "";
        q.navPageID = response.data ? response.data.navPageID : null;
        commit("setQuestionnaire", { q: q, t: payload.t });
        return false;
      } catch (e) {
        const q = Object.assign({}, state.Questionnaires[payload.t]);
        q.errors = ["Something went wrong!"];
        q.navButtonText = "Click to retry.";
        q.navPageID = null;
        commit("setQuestionnaire", { q: q, t: payload.t });
        return false;
      }
    },

    async UpdateQuestLastScreen({ commit }, payload: { qtid: number; ls: number }) {
      try {
        const resp = await api.post("/api/Home/UpdateQuestLastScreen", null, {
          params: {
            questType: payload.qtid,
            lastScreen: payload.ls,
          },
        });
        if (resp) {
          return resp.data;
        }
      } catch (e) {
        console.error("Error updating quest last screen:", e);
      }
    },

    async SaveSubChargePayment({ commit, state }, payload: { q: QuestionnaireViewModel; t: number; l?: boolean; a: number }) {
      try {
        const p = Object.assign({}, state.Payments[payload.t]);
        const response = await api.post(`/api/Home/SaveSubChargePayment?amt=${payload.a}`, payload.q);
        if (response && response.data && response.data.success) {
          p.quest = payload.q;
          p.errors = [];
          p.navButtonText = "";
          p.navPageID = null;
          commit("setPaymentQuest", { q: p.quest, t: payload.t });
          return true;
        }
        p.errors = response.data ? response.data.errors : [];
        p.navButtonText = response.data ? response.data.navButtonText : "";
        p.navPageID = response.data ? response.data.navPageID : null;
        commit("setPayment", { p: p, t: payload.t });
        return false;
      } catch (e) {
        const p = Object.assign({}, state.Payments[payload.t]);
        p.errors = ["Something went wrong!"];
        p.navButtonText = "Click to retry.";
        p.navPageID = null;
        commit("setPayment", { p: p, t: payload.t });
        return false;
      }
    },
    async SavePayment({ commit, state }, payload: { q: QuestionnaireViewModel; t: number; l?: boolean }) {
      try {
        const p = Object.assign({}, state.Payments[payload.t]);
        const response = await api.post(`/api/Home/SavePayment`, payload.q);
        if (response && response.data && response.data.success) {
          p.quest = payload.q;
          p.errors = [];
          p.navButtonText = "";
          p.navPageID = null;
          commit("setPaymentQuest", { q: p.quest, t: payload.t });
          return true;
        }
        p.errors = response.data ? response.data.errors : [];
        p.navButtonText = response.data ? response.data.navButtonText : "";
        p.navPageID = response.data ? response.data.navPageID : null;
        commit("setPayment", { p: p, t: payload.t });
        return false;
      } catch (e) {
        const p = Object.assign({}, state.Payments[payload.t]);
        p.errors = ["Something went wrong!"];
        p.navButtonText = "Click to retry.";
        p.navPageID = null;
        commit("setPayment", { p: p, t: payload.t });
        return false;
      }
    },
    async SaveRxPayment({ commit, state }, payload: { r: Rx }) {
      try {
        const p = Object.assign({}, state.RxPayments[payload.r.id]);
        const response = await api.post(`/api/Home/SaveRxPayment`, payload.r);
        if (response && response.data && response.data.success) {
          p.rx = payload.r;
          p.errors = [];
          p.navButtonText = "";
          p.navPageID = null;
          commit("setRxPayment", { p: p, rxID: payload.r.id });
          return true;
        }
        p.errors = response.data ? response.data.errors : [];
        p.navButtonText = response.data ? response.data.navButtonText : "";
        p.navPageID = response.data ? response.data.navPageID : null;
        commit("setRxPayment", { p: p, rxID: payload.r.id });
        return false;
      } catch (e) {
        const p = Object.assign({}, state.RxPayments[payload.r.id]);
        p.errors = ["Something went wrong!"];
        p.navButtonText = "Click to retry.";
        p.navPageID = null;
        commit("setRxPayment", { p: p, rxID: payload.r.id });
        return false;
      }
    },

    async SaveMarketplaceRxPayment({ commit, state }, payload: MarketplaceRxPaymentViewModel) {
      try {
        const response = await api.post(`/api/medicationmarketplace/SaveRxPayment`, payload);
        if (response && response.data) {
          return response.data;
        }
      } catch (e) {
        return false;
      }
    },

    async SaveMarketplaceSubscriptionPayment({ commit, state }, payload: { affiliateSubscription: AffiliateSubscription; isMonthly: boolean; authToken: string }) {
      try {
        const response = await api.post(`/api/medicationmarketplace/SaveSubscriptionPayment`, payload);
        if (response && response.data) {
          return response.data;
        }
      } catch (e) {
        return false;
      }
    },
    async UpdateMarketplaceSubscriptionPayment({ commit, state }, payload: { affiliateSubscription: AffiliateSubscription; isMonthly: boolean; authToken: string }) {
      try {
        const response = await api.post(`/api/medicationmarketplace/UpdateSubscriptionPayment`, payload);
        if (response && response.data) {
          return response.data;
        }
      } catch (e) {
        return false;
      }
    },

    async SaveRxRefill({ commit, state }, payload: { r: Rx }) {
      try {
        const p = Object.assign({}, state.RxRefills[payload.r.id]);
        const response = await api.post(`/api/Home/SaveRxPayment`, payload.r);
        if (response && response.data && response.data.success) {
          p.rx = payload.r;
          p.errors = [];
          p.navButtonText = "";
          p.navPageID = null;
          commit("setRxRefill", { r: p, rxID: payload.r.id });
          return true;
        }
        p.errors = response.data ? response.data.errors : [];
        p.navButtonText = response.data ? response.data.navButtonText : "";
        p.navPageID = response.data ? response.data.navPageID : null;
        commit("setRxRefill", { r: p, rxID: payload.r.id });
        return false;
      } catch (e) {
        const p = Object.assign({}, state.RxRefills[payload.r.id]);
        p.errors = ["Something went wrong!"];
        p.navButtonText = "Click to retry.";
        p.navPageID = null;
        commit("setRxRefill", { r: p, rxID: payload.r.id });
        return false;
      }
    },
    async SaveModify({ commit, state }, payload: { rVM: RefillViewModel; num: number }) {
      try {
        const q = Object.assign({}, state.Refills[payload.rVM.quest.questionnaireTypeID]);
        const response = await api.post(`/api/Home/SaveModify?num=${payload.num}`, { quest: payload.rVM.quest, refillQuestionsObj: payload.rVM.refillQuestionsObj });
        if (response && response.data && response.data.success) {
          q.quest = response.data.data;
          commit("setRefill", { r: q, t: payload.rVM.quest.questionnaireTypeID });
          return true;
        }
        q.errors = response.data ? response.data.errors : [];
        q.navButtonText = response.data ? response.data.navButtonText : "";
        q.navPageID = response.data ? response.data.navPageID : null;
        commit("setRefill", { r: q, t: payload.rVM.quest.questionnaireTypeID });
        return false;
      } catch (e) {
        const q = Object.assign({}, state.Refills[payload.rVM.quest.questionnaireTypeID]);
        q.errors = ["Something went wrong!"];
        q.navButtonText = "Click to retry.";
        q.navPageID = null;
        commit("setRefill", { r: q, t: payload.rVM.quest.questionnaireTypeID });
        return false;
      }
    },
    async SaveRefill({ commit, state }, payload: { rVM: RefillViewModel; num: number }) {
      try {
        const q = Object.assign({}, state.Refills[payload.rVM.quest.questionnaireTypeID]);
        const response = await api.post(`/api/Home/SaveRefill?num=${payload.num}`, { quest: payload.rVM.quest, refillQuestionsObj: payload.rVM.refillQuestionsObj });
        if (response && response.data && response.data.success) {
          q.quest = response.data.data;
          commit("setRefill", { r: q, t: payload.rVM.quest.questionnaireTypeID });
          return true;
        }
        q.errors = response.data ? response.data.errors : [];
        q.navButtonText = response.data ? response.data.navButtonText : "";
        q.navPageID = response.data ? response.data.navPageID : null;
        commit("setRefill", { r: q, t: payload.rVM.quest.questionnaireTypeID });
        return false;
      } catch (e) {
        const q = Object.assign({}, state.Refills[payload.rVM.quest.questionnaireTypeID]);
        q.errors = ["Something went wrong!"];
        q.navButtonText = "Click to retry.";
        q.navPageID = null;
        commit("setRefill", { r: q, t: payload.rVM.quest.questionnaireTypeID });
        return false;
      }
    },
    async SaveRefillQuestions({ commit, state }, payload: { q: Questions; t: number; l?: boolean; rxID?: number }) {
      try {
        const q: RefillViewModel = Object.assign({}, state.Refills[payload.t]);
        const response = await api.post(`/api/Home/SaveRefillQuestions?t=${payload.t}&isLastQuestPage=${payload.l ? "true" : "false"}${payload.rxID ? "&rxID=" + payload.rxID : ""}`, payload.q);
        if (response && response.data && response.data.success) {
          q.refillQuestionsObj = payload.q;
          q.errors = response.data ? response.data.errors : [];
          q.navButtonText = response.data ? response.data.navButtonText : "";
          q.navPageID = response.data ? response.data.navPageID : null;
          if (response.data.data && (response.data.data.isHardStop || response.data.data.hardStop)) {
            q.quest.hardStop = true;
            q.hardStop = true;
            commit("setRefill", { r: q, t: payload.t });
            return false;
          }
          commit("setRefill", { r: q, t: payload.t });
          return response.data.data;
        }
        if (response && response.data && (response.data.data.refillHeld || response.data.data.hardStop)) {
          q.errors = response.data ? response.data.errors : [];
          q.navButtonText = response.data ? response.data.navButtonText : "";
          q.navPageID = response.data ? response.data.navPageID : null;
          q.refillHeld = response.data.data.refillHeld ? true : false;
          q.hardStop = response.data.data.hardStop ? true : false;
        } else {
          q.errors = ["Something went wrong!"];
          q.navButtonText = "Click to retry.";
          q.navPageID = null;
        }
        commit("setRefill", { r: q, t: payload.t });
        return false;
      } catch (e) {
        const q = Object.assign({}, state.Refills[payload.t]);
        q.errors = ["Something went wrong!"];
        q.navButtonText = "Click to retry.";
        q.navPageID = null;
        commit("setRefill", { r: q, t: payload.t });
        return false;
      }
    },
    async Refresh({ commit, state, dispatch }) {
      try {
        if (!state.RefreshToken) return true; //skipping refresh one is already in progress
        const postData = { token: state.Token, refreshToken: state.RefreshToken };
        commit("setRefreshToken", null);
        const response = await axios.post("/api/members/refresh", postData);
        if (response && response.data) {
          commit("setToken", response.data.token);
          commit("setRefreshToken", response.data.refreshToken);
          commit("setRole", response.data.role);
          commit("setGrantedPermissions", response.data.permissions);
          commit("setPhotoPath", response.data.photoPath);
          commit("setFirstName", response.data.firstName);
          commit("setLastName", response.data.lastName);
          commit("setPatientAffiliateSubscription", response.data.patientAffiliateSubscription);
          return true;
        }
        dispatch("SignOut");
        if (router.currentRoute.name != "signin") router.push({ name: "signin", query: { r: router.currentRoute.fullPath } });
        return false;
      } catch (e) {
        dispatch("SignOut");
        if (router.currentRoute.name != "signin") router.push({ name: "signin", query: { r: router.currentRoute.fullPath } });
        return false;
      }
    },
    async SignIn({ commit }, { u, p }) {
      try {
        const response = await axios.post("/api/members/authentication", { u, p });
        if (response && response.data) {
          if (!response.data.twoFactorEnabled) {
            commit("setToken", response.data.token);
            commit("setRefreshToken", response.data.refreshToken);
            commit("setRole", response.data.role);
            commit("setPhotoPath", response.data.photoPath);
            commit("setGrantedPermissions", response.data.permissions);
            commit("setFirstName", response.data.firstName);
            commit("setLastName", response.data.lastName);
            commit("setPatientAffiliateSubscription", response.data.patientAffiliateSubscription);
            commit("setEmail", response.data.email);
            return { status: true };
          }
          return { status: true, token: response.data.token, twoFactorEnabled: response.data.twoFactorEnabled, authMethods: response.data.authMethods };
        }
        return { status: false };
      } catch (e) {
        return { status: false };
      }
    },
    async SignInMFA({ commit }, { c, method, token }) {
      let err = false;
      const response = await axios.post("/api/members/twofactor", { c, method }, { headers: { Authorization: `Bearer ${token}` } }).catch(function (e) {
        if (e.response && e.response.status == 401) {
          err = true;
        }
      });
      if (err) {
        return null;
      }
      try {
        if (response && response.data?.token) {
          commit("setToken", response.data.token);
          commit("setRefreshToken", response.data.refreshToken);
          commit("setRole", response.data.role);
          commit("setPhotoPath", response.data.photoPath);
          commit("setGrantedPermissions", response.data.permissions);
          commit("setFirstName", response.data.firstName);
          commit("setLastName", response.data.lastName);
          commit("setPatientAffiliateSubscription", response.data.patientAffiliateSubscription);
          commit("setEmail", response.data.email);
          return true;
        }
        return false;
      } catch (e) {
        return false;
      }
    },
    async AuthTwoFactorOnly({ commit }, payload) {
      let err = false;
      const response = await axios.post("/api/members/AuthTwoFactorOnly", payload).catch(function (e) {
        if (e.response && e.response.status == 401) {
          err = true;
        }
      });
      if (err) {
        return null;
      }
      try {
        if (response && response.data?.token) {
          commit("setToken", response.data.token);
          commit("setRefreshToken", response.data.refreshToken);
          commit("setRole", response.data.role);
          commit("setPhotoPath", response.data.photoPath);
          commit("setGrantedPermissions", response.data.permissions);
          commit("setFirstName", response.data.firstName);
          commit("setLastName", response.data.lastName);
          commit("setPatientAffiliateSubscription", response.data.patientAffiliateSubscription);
          commit("setEmail", response.data.email);
          return response.data;
        }
        return false;
      } catch (e) {
        return false;
      }
    },
    async GetSamCartQuestionnaire({ state, commit }, payload) {
      try {
        const response = await axios.post("/api/home/GetSamCartQuestionnaire", payload);
        if (response && response.data) {
          return response;
        }
        return false;
      } catch (e) {
        return false;
      }
    },
    async GetTelegraVisitURL({ state, commit }, payload) {
      try {
        const response = await api.get(`/api/home/GetTelegraVisitURL?questionnaireTypeID=${payload.questionnaireTypeID}`);
        if (response && response.data) {
          return response.data;
        }
        return false;
      } catch (e) {
        return false;
      }
    },
    async SendTwoFactorOnly({ state, commit }, payload) {
      try {
        const response = await axios.post("/api/members/SendTwoFactorOnly", payload);
        if (response && response.data) {
          return true;
        }
        return false;
      } catch (e) {
        return false;
      }
    },
    async TwoFactorToken({ state, commit }, { method, token }) {
      try {
        const response = await axios.post("/api/members/twofactortoken", { method }, { headers: { Authorization: `Bearer ${token ? token : state.Token}` } });
        if (response && response.data) {
          return true;
        }
        return false;
      } catch (e) {
        return false;
      }
    },
    async SignInOpenID({ commit }, vm) {
      try {
        commit("setToken", vm.Token);
        commit("setRefreshToken", vm.RefreshToken);
        commit("setRole", vm.Role);
        commit("setPhotoPath", vm.PhotoPath);
        commit("setFirstName", vm.FirstName);
        commit("setLastName", vm.LastName);
        commit("setEmail", vm.Email);
      } catch (e) {
        console.log(e);
      }
    },
    clearState({ commit }) {
      commit("setShowMainMenu", false);
      commit("setCustomerLookup", []);
      commit("setSidebarAdmin", false);
      commit("setShowCart", false);
      commit("setPharmacyCart", false);
      commit("setPassedPromoCode", null);
      commit("setUTM", null);
      commit("setShippingCarriers", []);
      commit("setEmail", null);
      commit("setLastName", null);
      commit("setFirstName", null);
      commit("setPhotoPath", null);
      commit("setCurrentAddRxPatient", null);
      commit("setSiteSetupTimeStamp", null);
      commit("setAffiliate", {});
      commit("setAffiliateFeatures", {});
      commit("setRole", "");
      commit("setToken", "");
      commit("setRefreshToken", "");
      commit("setQuestionnaireTypeProducts", []);
      commit("setOTCProducts", []);
      commit("setGrantedPermissions", []);
      commit("setRoles", []);
      commit("setShoppingCart", {} as ShoppingCartViewModel);
      commit("setShowCart", false);
      commit("setLoading", false);
      commit("setBypassAppLoading", false);
      commit("setQuestionnaires", []);
      commit("setPayment", {});
      commit("setRefill", {});
      commit("setPhotos", {});
      commit("setRxPayment", {});
      commit("setRxRefill", {});
      commit("setSubscriptionPlan", {});
      commit("setCurrentQuestType", 0);
      commit("setCurrentRxID", null);
      commit("setCurrentSubscriptionProgramID", null);
      commit("setCurrentRefillNum", null);
      commit("setFullPatientWidgetData", null);
      commit("setIsVerifiedQuickLink", false);
      commit("setIsQuickLinkUser", false);
      commit("setIsQuickLinkSupportUser", false);
      commit("setQuickLinkExternalID", null);
      commit("setPatientAffiliateSubscriptionUpdate", {});
      commit("setPatientAffiliateSubscription", {});
      sessionStorage.clear();
    },
    async SignOut({ commit, dispatch }) {
      const holder = sessionStorage.getItem("s");
      const objHolder = JSON.parse(holder!);
      await dispatch("clearState");
      commit("setTheme", objHolder.Theme);
      await dispatch("getSiteSetup");
    },
    async GetAccountSettings({ commit }): Promise<AjaxReturnViewModel<AccountSettingsViewModel> | null> {
      try {
        const settings = await api.get("/api/members/accountsettings");
        if (settings?.data) {
          return settings.data.data;
        }
      } catch (e) {
        console.log(e);
      }
      return null;
    },
    async UpdateAccountSettings({ commit }, newSettings: AccountSettingsViewModel): Promise<AjaxReturnViewModel<AccountSettingsViewModel> | null> {
      try {
        const settings = await api.post("/api/members/accountsettings", newSettings);
        if (settings?.data) {
          return settings.data.data;
        }
      } catch (e) {
        console.log(e);
      }
      return null;
    },
    async setupTwoFactor({ commit }, payload) {
      try {
        const settings = await api.post("/api/members/setupTwoFactor", payload);
        if (settings?.data) {
          return settings.data;
        }
        return null;
      } catch (e) {
        console.log(e);
      }
    },
    async disableTwoFactor({ commit }) {
      try {
        const settings = await api.get("/api/members/disableTwoFactor");
        if (settings?.data) {
          return settings.data.data;
        }
        return null;
      } catch (e) {
        console.log(e);
      }
    },
    async GetFreqsByProductRxID({ commit }, productRxID: number) {
      try {
        const result = await api.get(`/api/Home/GetFreqsByProductRxID?productRxID=${productRxID}`);
        return result.data;
      } catch (e) {
        console.log(e);
      }
      return null;
    },
    async getSiteSetup({ commit, state }) {
      if (state.SiteSetupTimeStamp && new Date(new Date(state.SiteSetupTimeStamp).getTime() + 30 * 60000) > new Date()) return;
      try {
        const siteSetup = await api.get("/api/Home/GetSiteSetup");
        if (!siteSetup || !siteSetup.data) return;
        if (siteSetup.data.affiliate.themeJSON) commit("setTheme", JSON.parse(siteSetup.data.affiliate.themeJSON));
        if (siteSetup.data.affiliate.contentJSON) commit("setContent", JSON.parse(siteSetup.data.affiliate.contentJSON));
        if (siteSetup.data.questionnaireTypeProducts) commit("setQuestionnaireTypeProducts", siteSetup.data.questionnaireTypeProducts);
        if (siteSetup.data.otcProducts) commit("setOTCProducts", siteSetup.data.otcProducts);
        if (siteSetup.data.roles) commit("setRoles", siteSetup.data.roles);
        if (siteSetup.data.affiliate) commit("setAffiliate", siteSetup.data.affiliate);
        if (siteSetup.data.affiliateFeatures) commit("setAffiliateFeatures", siteSetup.data.affiliateFeatures);
        commit("setSiteSetupTimeStamp", new Date());
      } catch (e) {
        console.log(e);
      }
    },
    async FWDMSGToSupport({ commit, state }, payload: { patientMessageID: number; patientAffiliateID: number }) {
      try {
        const result = await api.post(`/api/PatientMessages/FWDMSGToSupport?patientMessageID=${payload.patientMessageID}&patientAffiliateID=${payload.patientAffiliateID}`);
        if (!result || !result.data || !result.data.success) return false;
        return true;
      } catch (e) {
        console.log(e);
        return false;
      }
    },
    async FWDMSGToPhysician({ commit, state }, payload: { patientMessageID: number; patientAffiliateID: number; questionnaireTypeID: number; patientUserID: number }) {
      try {
        const result = await api.post(`/api/PatientMessages/FWDMSGToPhysician?patientMessageID=${payload.patientMessageID}&patientAffiliateID=${payload.patientAffiliateID}&questionnaireTypeID=${payload.questionnaireTypeID}&patientUserID=${payload.patientUserID}`);
        if (!result || !result.data || !result.data.success) return false;
        return true;
      } catch (e) {
        console.log(e);
        return false;
      }
    },
    async GetCannedMessages({ commit, state }) {
      try {
        const result = await api.get(`/api/PatientMessages/GetCannedMessages`);
        if (!result || !result.data || !result.data.success) return null;
        return result.data.data;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    async GetPatientMessages({ commit, state }, payload: { patientId: number; patientAffiliateID: number; questionnaireTypeID: number }) {
      try {
        const result = await api.post(`/api/PatientMessages/GetPatientMessages?patientId=${payload.patientId}&patientAffiliateID=${payload.patientAffiliateID}&questionnaireTypeID=${payload.questionnaireTypeID}`);
        if (!result || !result.data || !result.data.success) return null;
        return result.data.data;
      } catch (e) {
        console.log(e);
        return null;
      }
    },

    async GetPatientMessagesForMedicalRecord({ commit, state }, payload: { patientId: number; patientAffiliateID: number }) {
      try {
        const result = await api.post(`/api/PatientMessages/GetPatientMessagesForMedicalRecord?patientId=${payload.patientId}&patientAffiliateID=${payload.patientAffiliateID}`);
        if (!result || !result.data || !result.data.success) return null;
        return result.data.data;
      } catch (e) {
        console.log(e);
        return null;
      }
    },

    async GetSupportUserInbox({ commit, state }, payload: { questionnaireTypeID: number; pageNumber: number; pageSize: number; filter: string }) {
      try {
        const result = await api.post(`/api/support/UserInbox?questionnaireTypeID=${payload.questionnaireTypeID}&pageSize=${payload.pageSize}&pageNumber=${payload.pageNumber}&filter=${payload.filter}`);
        if (!result || !result.data || !result.data.success) return null;
        return result.data.data;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    async GetQuestionnaireByID({ commit, state }, payload: { ID: number }) {
      try {
        const result = await api.post(`/api/AffiliateAdmin/GetQuestionnaireByID?ID=${payload.ID}`);
        if (!result || !result.data || !result.data.success) return null;
        return result.data.data;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    async GetQuestionsByAffiliateQuestionnaireType({ commit, state }, payload: { ID: number }) {
      try {
        let postUrl = "";
        if (state.Role == "AffiliateAdmin") postUrl = `/api/affiliateadmin/UserQuestionsJSONDrafts?affiliateQuestionnaireTypeID=${payload.ID}`;
        if (state.Role == "Admin") postUrl = `/api/Admin/UserQuestionsJSONDrafts?affiliateQuestionnaireTypeID=${payload.ID}`;
        const result = await api.get(postUrl);
        if (!result || !result.data || !result.data.success) return null;
        return result.data.data;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    async MarkPatientMessageAsRead({ commit, state }, payload: { messageId: number; patientAffiliateID: number }) {
      try {
        const result = await api.post(`/api/PatientMessages/MarkPatientMessageAsRead?messageId=${payload.messageId}&patientAffiliateID=${payload.patientAffiliateID}`);
        if (!result || !result.data || !result.data.success) return false;
        return true;
      } catch (e) {
        console.log(e);
        return false;
      }
    },
    async SendPatientMessage({ commit, state }, payload: { patientId: number; patientAffiliateID: number; questionnaireTypeID: number; message: string; folderType: number; rxID?: number | null; attachment?: string | null; attachmentType?: number | null }) {
      try {
        const bodyPayload = {
          patientId: payload.patientId,
          patientAffiliateID: payload.patientAffiliateID,
          message: payload.message,
          folderType: payload.folderType,
          rxID: payload.rxID || 0, // Provide a default value of 0 if rxID is not provided or is null
          attachment: payload.attachment || "", // Provide a default empty string if attachment is not provided or is null
          attachmentType: payload.attachmentType || null, // Provide null if attachmentType is not provided or is null
        };

        const result = await api.post(`/api/PatientMessages/SendPatientMessage`, bodyPayload);
        if (!result || !result.data || !result.data.success) return null;
        return result.data.data;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    async PreviewBulkSend({ commit, state }, payload: { selectionType: string; questionnaireTypeId: number; productRxId: number; customerType: string; affiliateId: number | null; userIds?: string | null }) {
      try {
        const bodyPayload = {
          selectionType: payload.selectionType,
          questionnaireTypeId: payload.questionnaireTypeId,
          productRxId: payload.productRxId,
          customerType: payload.customerType,
          userIds: payload.userIds,
          affiliateId: payload.affiliateId,
        };

        const result = await api.post(`/api/support/PreviewBulkSend`, bodyPayload);
        if (!result || !result.data || !result.data.success) return null;
        return result.data.data;
      } catch (e) {
        console.log(e);
        return null;
      }
    },

    async GetPromoByCode({ commit }, payload: { t: number | number; code: string; notInitialQuest?: null | boolean }) {
      try {
        const result = await api.get(`/api/Home/GetPromoByCode?t=${payload.t}&code=${payload.code}&notInitialQuest=${payload.notInitialQuest ? payload.notInitialQuest : false}`);
        console.log("GetPromoByCode");
        console.log(result.data);
        return result.data;
      } catch (e) {
        console.log(e);
      }
      return null;
    },
    async CheckSupportedState({ commit, state }, payload: { state: string; p: PatientViewModel; q: QuestionnaireViewModel | null; t: number; isRefill?: boolean; isPayment?: boolean; isPatientProfile?: boolean }) {
      try {
        let url = `/api/Home/CheckSupportedState?state=${payload.state}&t=${payload.t}`;
        if (payload.isPatientProfile) url = `/api/Home/CheckSupportedStateNoT?state=${payload.state}`;
        if (payload.isPatientProfile && state.Role == "Support") url = `/api/Support/CheckSupportedStateNoT?state=${payload.state}&patientUserID=${payload.p.userID}&patientAffiliateID=${payload.p.affiliateID}`;
        if (payload.isPatientProfile && state.Role == "Pharmacy") url = `/api/Pharmacy/CheckSupportedStateNoT?state=${payload.state}&patientUserID=${payload.p.userID}&patientAffiliateID=${payload.p.affiliateID}`;
        if (payload.isPatientProfile && state.Role == "AffiliateAdmin") url = `/api/affiliateadmin/CheckSupportedStateNoT?state=${payload.state}&patientUserID=${payload.p.userID}&patientAffiliateID=${payload.p.affiliateID}`;

        const result = await api.post(url);
        let q: RefillViewModel | QuestViewModel | PaymentViewModel | any = {};
        if (payload.isPayment) q = Object.assign({}, state.Payments[payload.t]);
        else if (payload.isRefill) q = Object.assign({}, state.Refills[payload.t]);
        else if (payload.isPatientProfile) q = null;
        else q = Object.assign({}, state.Questionnaires[payload.t]);

        payload.p.homeState = result.data && result.data.active ? result.data.stateName : !payload.isPatientProfile ? q.patient.homeState : payload.p.homeState;
        payload.p.state = result.data && result.data.active && ((payload.isPatientProfile && payload.p.shippingIsSame) || (q && q.patient.shippingIsSame)) ? result.data.stateName : !payload.isPatientProfile ? q.patient.state : payload.p.state;

        if (!payload.isPatientProfile) {
          q.patient = payload.p;
          q.visitRequired = result.data && result.data.visitRequired ? true : false;
          q.quest = payload.q ? payload.q : q.quest;
          const stateIdx = q.shippableStates.findIndex((x: SupportedState) => x.stateName == payload.state);
          if (result.data && stateIdx > -1) q.shippableStates.splice(stateIdx, 1, result.data);
          if ((!result.data.visitRequired && !result.data.allowSyncWhenVisitRequiredFalse) || (!result.data.visitRequired && q.quest.visitTypeID == VisitType.QuestionnaireOnly)) q.quest.visitTypeID = VisitType.QuestionnaireOnly; // Default to quest only
          else {
            if (result.data.physicianGroupScheduleURL && (q.quest.visitTypeID == VisitType.QuestionnaireOnly || q.quest.visitTypeID == VisitType.Scheduled)) q.quest.visitTypeID = VisitType.Scheduled; // Then Scheduled if available
            else {
              if (result.data.instantVisitPhysicianGroupID) q.quest.visitTypeID = VisitType.Instant; // Then Instant if available
            }
          }
        }

        if (payload.isPayment) commit("setPayment", { p: q, t: payload.t });
        else if (payload.isRefill) commit("setRefill", { r: q, t: payload.t });
        else if (payload.isPatientProfile) q = null;
        else commit("setQuestionnaire", { q: q, t: payload.t });

        return result.data && result.data.active ? true : false;
      } catch (e) {
        console.log(e);
      }
      return false;
    },
    async GetCheckout({ commit }): Promise<AjaxReturnViewModel<CheckoutViewModel> | null> {
      try {
        const vm = await api.get("/api/Home/GetCheckout");
        return vm.data;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    getStoredShoppingCart({ commit, state }) {
      try {
        const storedCart = localStorage.getItem("ShoppingCart");
        if (storedCart) {
          commit("setShoppingCart", JSON.parse(storedCart));
          return;
        }
      } catch (e) {
        console.log(e);
      }
    },
    async GetShoppingCart({ commit }) {
      try {
        const cart = await api.get("/api/Home/GetShoppingCart");
        if (cart?.data?.cart) {
          //commit("setShoppingCart", cart.data.cart);
          return cart.data.cart;
        }
      } catch (e) {
        console.log(e);
      }
    },
    async GetStarted({ commit, state }, payload: { t: number }) {
      const eq = Object.assign({}, state.Questionnaires[payload.t]);
      const q: QuestViewModel | any = eq ? eq : {};
      try {
        const response = await api.get(`/api/Home/GetStarted?t=${payload.t}`);
        if (response && response.data && response.data.success) {
          q.affiliateID = response.data.data.affiliateID;
          q.products = response.data.data.products;
          q.productGroups = response.data.data.productGroups;
          q.patientStates = response.data.data.patientStates;
          q.shippableStates = response.data.data.shippableStates;
          q.onlineDoctorConsultFee = response.data.data.onlineDoctorConsultFee;
          q.oldOnlineDoctorConsultFee = response.data.data.oldOnlineDoctorConsultFee;
          q.expeditedShippingFee = response.data.data.expeditedShippingFee;
          q.standardShippingFee = response.data.data.standardShippingFee;
          q.quest = q.quest && q.quest.qProductRxs && q.quest.qProductRxs.length > 0 ? q.quest : response.data.data.quest;
          q.chooseProductPriorToAuth = response.data.data.chooseProductPriorToAuth;
          q.getStartedProductsAfterQuestionnaire = response.data.data.getStartedProductsAfterQuestionnaire;
          q.confirmTerms = response.data.data.confirmTerms;
          q.giveProductRxSupplyChoice = response.data.data.giveProductRxSupplyChoice;
          commit("setQuestionnaire", { q: q, t: payload.t });
          return response.data;
        }
        q.errors = response.data ? response.data.errors : [];
        q.navButtonText = response.data ? response.data.navButtonText : "";
        q.navPageID = response.data ? response.data.navPageID : null;
        commit("setQuestionnaire", { q: q, t: payload.t });
        return { data: q };
      } catch (e) {
        q.errors = ["Something went wrong!"];
        q.navButtonText = "Click to retry.";
        q.navPageID = null;
        commit("setQuestionnaire", { q: q, t: payload.t });
        return { data: q };
      }
    },
    async GetQuestionnaire({ commit }, payload: { t: number }) {
      let q: QuestViewModel | any = {};
      try {
        const response = await api.get(`/api/Home/GetQuestionnaire?t=${payload.t}`);
        if (response && response.data && response.data.success) {
          q = response.data.data;
          commit("setQuestionnaire", { q: q, t: payload.t });
          return response.data;
        }
        q.errors = response.data ? response.data.errors : [];
        q.navButtonText = response.data ? response.data.navButtonText : "";
        q.navPageID = response.data ? response.data.navPageID : null;
        commit("setQuestionnaire", { q: q, t: payload.t });
        return { data: q };
      } catch (e) {
        q.errors = ["Something went wrong!"];
        q.navButtonText = "Click to retry.";
        q.navPageID = null;
        commit("setQuestionnaire", { q: q, t: payload.t });
        return { data: q };
      }
    },
    async GetSubscriptionPlan({ commit }, payload: { subscriptionProgramID: number }) {
      let q: SubscriptionPlanViewModel | any = {};
      try {
        const response = await api.get(`/api/Home/GetSubscriptionPlan?subscriptionProgramID=${payload.subscriptionProgramID}`);
        if (response && response.data && response.data.success) {
          q = response.data.data;
          commit("setSubscriptionPlan", { s: q, subscriptionProgramID: payload.subscriptionProgramID });
          return response.data;
        }
        q.errors = response.data ? response.data.errors : [];
        q.navButtonText = response.data ? response.data.navButtonText : "";
        q.navPageID = response.data ? response.data.navPageID : null;
        commit("setSubscriptionPlan", { s: q, subscriptionProgramID: payload.subscriptionProgramID });
        return { data: q };
      } catch (e) {
        q.errors = ["Something went wrong!"];
        q.navButtonText = "Click to retry.";
        q.navPageID = null;
        commit("setSubscriptionPlan", { s: q, subscriptionProgramID: payload.subscriptionProgramID });
        return { data: q };
      }
    },
    async GetSubChargePayment({ commit }, payload: { t: number }) {
      let p: PaymentViewModel | any = {};
      try {
        const response = await api.get(`/api/Home/GetSubChargePayment?t=${payload.t}`);
        if (response && response.data && response.data.success) {
          p = response.data.data;
          commit("setPayment", { p: p, t: payload.t });
          return response.data;
        }
        p.errors = response.data ? response.data.errors : [];
        p.navButtonText = response.data ? response.data.navButtonText : "";
        p.navPageID = response.data ? response.data.navPageID : null;
        commit("setPayment", { p: p, t: payload.t });
        return { data: p };
      } catch (e) {
        p.errors = ["Something went wrong!"];
        p.navButtonText = "Click to retry.";
        p.navPageID = null;
        commit("setPayment", { p: p, t: payload.t });
        return { data: p };
      }
    },
    async GetPayment({ commit }, payload: { t: number }) {
      let p: PaymentViewModel | any = {};
      try {
        const response = await api.get(`/api/Home/GetPayment?t=${payload.t}`);
        if (response && response.data && response.data.success) {
          p = response.data.data;
          commit("setPayment", { p: p, t: payload.t });
          return response.data;
        }
        p.errors = response.data ? response.data.errors : [];
        p.navButtonText = response.data ? response.data.navButtonText : "";
        p.navPageID = response.data ? response.data.navPageID : null;
        commit("setPayment", { p: p, t: payload.t });
        return { data: p };
      } catch (e) {
        p.errors = ["Something went wrong!"];
        p.navButtonText = "Click to retry.";
        p.navPageID = null;
        commit("setPayment", { p: p, t: payload.t });
        return { data: p };
      }
    },
    async GetPhotos({ commit }, payload: { t: number }) {
      let p: PhotosUpdateViewModel | any = {};
      try {
        const response = await api.get(`/api/Home/GetPhotos?t=${payload.t}`);
        if (response && response.data && response.data.success) {
          p = response.data.data;
          commit("setPhotos", { p: p, t: payload.t });
          return response.data;
        }
        p.errors = response.data ? response.data.errors : [];
        p.navButtonText = response.data ? response.data.navButtonText : "";
        p.navPageID = response.data ? response.data.navPageID : null;
        commit("setPhotos", { p: p, t: payload.t });
        return { data: p };
      } catch (e) {
        p.errors = ["Something went wrong!"];
        p.navButtonText = "Click to retry.";
        p.navPageID = null;
        commit("setPhotos", { p: p, t: payload.t });
        return { data: p };
      }
    },
    async GetRxRefill({ commit }, payload: { rxID: number; refillNum: number }) {
      let p: RxRefillViewModel | any = {};
      try {
        const response = await api.get(`/api/Home/GetRxRefill?rxID=${payload.rxID}&refillNum=${payload.refillNum}`);
        if (response && response.data && response.data.success) {
          p = response.data.data;
          commit("setRxRefill", { r: p, rxID: payload.rxID });
          return response.data;
        }
        p.errors = response.data ? response.data.errors : [];
        p.navButtonText = response.data ? response.data.navButtonText : "";
        p.navPageID = response.data ? response.data.navPageID : null;
        commit("setRxRefill", { r: p, rxID: payload.rxID });
        return { data: p };
      } catch (e) {
        p.errors = ["Something went wrong!"];
        p.navButtonText = "Click to retry.";
        p.navPageID = null;
        commit("setRxRefill", { r: p, rxID: payload.rxID });
        return { data: p };
      }
    },
    async GetRxPayment({ commit }, payload: { rxID: number }) {
      let p: RxPaymentViewModel | any = {};
      try {
        const response = await api.get(`/api/Home/GetRxPayment?rxID=${payload.rxID}`);
        if (response && response.data && response.data.success) {
          p = response.data.data;
          commit("setRxPayment", { p: p, rxID: payload.rxID });
          return response.data;
        }
        p.errors = response.data ? response.data.errors : [];
        p.navButtonText = response.data ? response.data.navButtonText : "";
        p.navPageID = response.data ? response.data.navPageID : null;
        commit("setRxPayment", { p: p, rxID: payload.rxID });
        return { data: p };
      } catch (e) {
        p.errors = ["Something went wrong!"];
        p.navButtonText = "Click to retry.";
        p.navPageID = null;
        commit("setRxPayment", { p: p, rxID: payload.rxID });
        return { data: p };
      }
    },
    async GetModify({ commit }, payload: { t: number; num: number }) {
      let r: RefillViewModel | any = {};
      try {
        const response = await api.get(`/api/Home/GetModify?t=${payload.t}&num=${payload.num}`);
        if (response && response.data && response.data.success) {
          r = response.data.data;
          r.errors = response.data ? response.data.errors : [];
          r.navButtonText = response.data ? response.data.navButtonText : "";
          r.navPageID = response.data ? response.data.navPageID : null;
          commit("setRefill", { r: r, t: payload.t });
          return response.data;
        }
        r.errors = response.data ? response.data.errors : [];
        r.navButtonText = response.data ? response.data.navButtonText : "";
        r.navPageID = response.data ? response.data.navPageID : null;
        commit("setRefill", { r: r, t: payload.t });
        return { data: r };
      } catch (e) {
        r.errors = ["Something went wrong!"];
        r.navButtonText = "Click to retry.";
        r.navPageID = null;
        commit("setRefill", { r: r, t: payload.t });
        return { data: r };
      }
    },
    async GetRefill({ commit }, payload: { t: number; num: number }) {
      let r: RefillViewModel | any = {};
      try {
        const response = await api.get(`/api/Home/GetRefill?t=${payload.t}&num=${payload.num}`);
        if (response && response.data && response.data.success) {
          r = response.data.data;
          r.errors = response.data ? response.data.errors : [];
          r.navButtonText = response.data ? response.data.navButtonText : "";
          r.navPageID = response.data ? response.data.navPageID : null;
          commit("setRefill", { r: r, t: payload.t });
          return response.data;
        }
        r.errors = response.data ? response.data.errors : [];
        r.navButtonText = response.data ? response.data.navButtonText : "";
        r.navPageID = response.data ? response.data.navPageID : null;
        commit("setRefill", { r: r, t: payload.t });
        return { data: r };
      } catch (e) {
        r.errors = ["Something went wrong!"];
        r.navButtonText = "Click to retry.";
        r.navPageID = null;
        commit("setRefill", { r: r, t: payload.t });
        return { data: r };
      }
    },
    async GetPatientPortal({ commit }): Promise<AjaxReturnViewModel<PatientPortalViewModel> | null> {
      try {
        const vm = await api.get("/api/Home/GetPatientPortal");
        return vm.data;
      } catch (e) {
        console.log(e);
        return null;
      }
    },

    async GetMarketplaceSubscriptionManagement({ commit }): Promise<AjaxReturnViewModel<MedicationMarketplaceSubscriptionManagementViewModel> | null> {
      try {
        const vm = await api.get("/api/medicationmarketplace/GetMarketplaceSubscriptionManagement");
        return vm.data;
      } catch (e) {
        console.log(e);
        return null;
      }
    },

    async GetMarketplaceAffiliateSubscriptions({ commit }): Promise<AjaxReturnViewModel<AffiliateSubscription[]> | null> {
      try {
        const vm = await api.get("/api/medicationmarketplace/GetMarketplaceAffiliateSubscriptions");
        return vm.data;
      } catch (e) {
        console.log(e);
        return null;
      }
    },

    async GetMedicationMarketplaceCheckout({ commit }, rxArray) {
      try {
        if (!Array.isArray(rxArray)) {
          console.error("Invalid parameter: rxArray must be an array.");
          return null;
        }
        const response = await api.post("/api/medicationmarketplace/GetMedicationMarketplaceCheckout", rxArray);
        return response.data;
      } catch (e) {
        console.error("Error fetching Medication Marketplace Checkout:", e);
        return null;
      }
    },

    async UpdatePatientAllergiesMedications({ commit }, payload) {
      try {
        const response = await api.post("/api/medicationmarketplace/UpdatePatientAllergiesMedications", payload);
        return response.data;
      } catch (e) {
        console.error(e);
        return null;
      }
    },

    async GetCancelOrderView({ commit, state }, payload: { patientUserID: number; patientAffiliateID: number; questionnaireTypeID: number; questionnaireID: number }): Promise<AjaxReturnViewModel<object> | null> {
      try {
        let postUrl = "";
        if (state.Role == "Support") postUrl = `/api/support/GetCancelOrderView?patientUserID=${payload.patientUserID}&patientAffiliateID=${payload.patientAffiliateID}&questionnaireTypeID=${payload.questionnaireTypeID}&questionnaireID=${payload.questionnaireID}`;
        if (state.Role == "AffiliateAdmin") postUrl = `/api/affiliateadmin/GetCancelOrderView?patientUserID=${payload.patientUserID}&patientAffiliateID=${payload.patientAffiliateID}&questionnaireTypeID=${payload.questionnaireTypeID}&questionnaireID=${payload.questionnaireID}`;
        const vm = await api.post(postUrl);
        return vm.data;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    async ResendToPhysicianAPI({ commit, state }, payload: { patientUserID: number; patientAffiliateID: number; questionnaireTypeID: number }): Promise<AjaxReturnViewModel<object> | null> {
      try {
        let postUrl = "";
        if (state.Role == "Support") postUrl = `/api/support/ResendToPhysicianAPI?patientUserID=${payload.patientUserID}&patientAffiliateID=${payload.patientAffiliateID}&questionnaireTypeID=${payload.questionnaireTypeID}`;
        if (state.Role == "AffiliateAdmin") postUrl = `/api/affiliateadmin/ResendToPhysicianAPI?patientUserID=${payload.patientUserID}&patientAffiliateID=${payload.patientAffiliateID}&questionnaireTypeID=${payload.questionnaireTypeID}`;
        const vm = await api.post(postUrl);
        return vm.data;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    async ChangePreferredRx({ commit, state }, payload: { patientUserID: number; patientAffiliateID: number; questionnaireTypeID: number; newProductRxID: number }): Promise<AjaxReturnViewModel<object> | null> {
      try {
        let postUrl = "";
        if (state.Role == "Support") postUrl = `/api/support/ChangePreferredRx?patientUserID=${payload.patientUserID}&patientAffiliateID=${payload.patientAffiliateID}&questionnaireTypeID=${payload.questionnaireTypeID}&newProductRxID=${payload.newProductRxID}`;
        if (state.Role == "AffiliateAdmin") postUrl = `/api/affiliateadmin/ChangePreferredRx?patientUserID=${payload.patientUserID}&patientAffiliateID=${payload.patientAffiliateID}&questionnaireTypeID=${payload.questionnaireTypeID}&newProductRxID=${payload.newProductRxID}`;
        const vm = await api.post(postUrl);
        return vm.data;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    async GetProductRxsForChangePreferredRx({ commit, state }, payload: { patientUserID: number; patientAffiliateID: number; questionnaireTypeID: number }): Promise<AjaxReturnViewModel<object> | null> {
      try {
        let postUrl = "";
        if (state.Role == "Support") postUrl = `/api/support/GetProductRxsForChangePreferredRx?patientUserID=${payload.patientUserID}&patientAffiliateID=${payload.patientAffiliateID}&questionnaireTypeID=${payload.questionnaireTypeID}`;
        if (state.Role == "AffiliateAdmin") postUrl = `/api/affiliateadmin/GetProductRxsForChangePreferredRx?patientUserID=${payload.patientUserID}&patientAffiliateID=${payload.patientAffiliateID}&questionnaireTypeID=${payload.questionnaireTypeID}`;
        const vm = await api.post(postUrl);
        return vm.data;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    async RestartTherapy({ commit, state }, payload: { patientUserID: number; patientAffiliateID: number; questionnaireTypeID: number }): Promise<AjaxReturnViewModel<object> | null> {
      try {
        let postUrl = "";
        if (state.Role == "Support") postUrl = `/api/support/RestartTherapy?patientUserID=${payload.patientUserID}&patientAffiliateID=${payload.patientAffiliateID}&questionnaireTypeID=${payload.questionnaireTypeID}`;
        if (state.Role == "AffiliateAdmin") postUrl = `/api/affiliateadmin/RestartTherapy?patientUserID=${payload.patientUserID}&patientAffiliateID=${payload.patientAffiliateID}&questionnaireTypeID=${payload.questionnaireTypeID}`;
        const vm = await api.post(postUrl);
        return vm.data;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    async GetRxHistoryByUserID({ commit, state }, payload: { patientUserID: number; patientAffiliateID: number }): Promise<AjaxReturnViewModel<PatientPortalRxContainer[]> | null> {
      try {
        let getUrl = "/api/Home/GetRxHistoryByUserID";
        if (state.Role == "Support") getUrl = `/api/Support/GetRxHistoryByUserID?patientUserID=${payload.patientUserID}&patientAffiliateID=${payload.patientAffiliateID}`;
        if (state.Role == "Physician") getUrl = `/api/physician/GetRxHistoryByUserID?patientUserID=${payload.patientUserID}&patientAffiliateID=${payload.patientAffiliateID}`;
        if (state.Role == "AffiliateAdmin") getUrl = `/api/affiliateadmin/GetRxHistoryByUserID?patientUserID=${payload.patientUserID}&patientAffiliateID=${payload.patientAffiliateID}`;

        const vm = await api.get(getUrl);
        return vm.data;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    async PatientRegister({ commit, state }, vm: PatientRegisterViewModel): Promise<AjaxReturnViewModel<Patient> | null> {
      try {
        if (state.PassedPromoCode) {
          vm.promoCode = state.PassedPromoCode;
        }
        const pVM = await api.post("/api/Home/PatientRegister", vm);
        return pVM.data as AjaxReturnViewModel<Patient>;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    async MedicationMarketplacePatientRegister({ commit, state }, vm: PatientRegisterViewModel): Promise<AjaxReturnViewModel<Patient> | null> {
      try {
        if (state.PassedPromoCode) {
          vm.promoCode = state.PassedPromoCode;
        }
        const pVM = await api.post("/api/medicationmarketplace/PatientRegister", vm);
        return pVM.data as AjaxReturnViewModel<Patient>;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    async CancelOrder({ commit, state }, vm: any): Promise<AjaxReturnViewModel<any> | null> {
      try {
        let postUrl = "";
        if (state.Role == "Support") postUrl = "/api/Support/CancelOrder";
        if (state.Role == "AffiliateAdmin") postUrl = "/api/affiliateadmin/CancelOrder";

        const pVM = await api.post(postUrl, vm);
        return pVM.data as AjaxReturnViewModel<any>;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    async GetNextPatientToPhotoVerify({ commit, state }) {
      try {
        let endpoint = "";

        if (state.Role === "PhotoVerify") {
          endpoint = "/api/photoverify/GetNextPatientToPhotoVerify";
        } else if (state.Role === "Support") {
          endpoint = "/api/support/GetNextPatientToPhotoVerify";
        }

        if (!endpoint) {
          throw new Error(`No endpoint defined for role: ${state.Role}`);
        }

        const pVM = await api.get(endpoint);
        return pVM.data as AjaxReturnViewModel<PhotoVerifyViewModel>;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    async GetNextPatientItem({ commit }, payload: { questionnaireID: number; affiliateID: number }) {
      try {
        const pVM = await api.get(`/api/Physician/GetNextPatientItem?questionnaireID=${payload.questionnaireID}&affiliateID=${payload.affiliateID}`);
        return pVM.data as AjaxReturnViewModel<PhysicianReviewDataViewModel>;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    async GetPatientMedicalRecord({ commit }, payload: { userID: number; affiliateID: number }) {
      try {
        const pVM = await api.get(`/api/support/GetPatientMedicalRecord?userID=${payload.userID}&affiliateID=${payload.affiliateID}`);
        return pVM.data as AjaxReturnViewModel<PatientMedicalRecordViewModel>;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    async GetVisitData({ commit }, payload: { startDate: number; endDate: number }) {
      try {
        const pVM = await api.post(`/api/Physician/GetVisitData?startDate=${payload.startDate}&endDate=${payload.endDate}`);
        return pVM.data as AjaxReturnViewModel<VisitScheduleViewModel[]>;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    async GetVisitScheduleReport({ commit }, payload: { startDate: number; endDate: number }) {
      try {
        const pVM = await api.post(`/api/Admin/GetVisitScheduleReport?startDate=${payload.startDate}&endDate=${payload.endDate}`);
        return pVM.data as AjaxReturnViewModel<VisitScheduleViewModel[]>;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    async DenyPhoto({ commit, state }, payload: { patientID: number; patientAffiliateID: number; photoReviewerComments: string }) {
      try {
        let endpoint = "";

        if (state.Role === "PhotoVerify") {
          endpoint = "/api/photoverify/DenyPhoto";
        } else if (state.Role === "Support") {
          endpoint = "/api/support/DenyPhoto";
        }

        if (!endpoint) {
          throw new Error(`No endpoint defined for role: ${state.Role}`);
        }

        const pVM = await api.post(`${endpoint}?patientID=${payload.patientID}&patientAffiliateID=${payload.patientAffiliateID}&photoReviewerComments=${payload.photoReviewerComments}`);
        return pVM.data as AjaxReturnViewModel<boolean>;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    async SkipPhoto({ commit, state }, payload: { patientID: number; patientAffiliateID: number; photoReviewSkipComments: string }) {
      try {
        let endpoint = "";

        if (state.Role === "PhotoVerify") {
          endpoint = "/api/photoverify/SkipPhoto";
        } else if (state.Role === "Support") {
          endpoint = "/api/support/SkipPhoto";
        }

        if (!endpoint) {
          throw new Error(`No endpoint defined for role: ${state.Role}`);
        }

        const pVM = await api.post(`${endpoint}?patientID=${payload.patientID}&patientAffiliateID=${payload.patientAffiliateID}&photoReviewSkipComments=${payload.photoReviewSkipComments}`);
        return pVM.data as AjaxReturnViewModel<boolean>;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    async ApprovePhoto({ commit, state }, payload: { patientID: number; patientAffiliateID: number; photoReviewerComments: string }) {
      try {
        let endpoint = "";

        if (state.Role === "PhotoVerify") {
          endpoint = "/api/photoverify/ApprovePhoto";
        } else if (state.Role === "Support") {
          endpoint = "/api/support/ApprovePhoto";
        }

        if (!endpoint) {
          throw new Error(`No endpoint defined for role: ${state.Role}`);
        }

        const pVM = await api.post(`${endpoint}?patientID=${payload.patientID}&patientAffiliateID=${payload.patientAffiliateID}&photoReviewerComments=${payload.photoReviewerComments}`);
        return pVM.data as AjaxReturnViewModel<boolean>;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    async SubmitRx({ commit }, payload) {
      try {
        //const pVM = await api.post(`/api/Physician/SubmitRx?questID=${payload.questID}&questAffiliateID=${payload.questAffiliateID}&visitPerformed=${payload.visitPerformed}&physicianComments=${payload.physicianComments}&soapNote=${payload.soapNote}`);
        const pVM = await api.post("/api/Physician/SubmitRx", payload);

        console.log("SubmitRx - Success");
        return pVM.data as AjaxReturnViewModel<boolean>;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    async ApproveRx({ commit }, payload: { questID: number; questAffiliateID: number; visitPerformed: boolean; physicianComments: string; soapNote: string }) {
      try {
        const pVM = await api.post(`/api/Physician/ApproveRx?questID=${payload.questID}&questAffiliateID=${payload.questAffiliateID}&visitPerformed=${payload.visitPerformed}&physicianComments=${payload.physicianComments}&soapNote=${payload.soapNote}`);
        return pVM.data as AjaxReturnViewModel<boolean>;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    async ApproveRefill({ commit }, payload: { questID: number; questAffiliateID: number; physicianComments: string; soapNote: string }) {
      try {
        const pVM = await api.post(`/api/Physician/ApproveRefill?questID=${payload.questID}&questAffiliateID=${payload.questAffiliateID}&physicianComments=${payload.physicianComments}&soapNote=${payload.soapNote}`);
        return pVM.data as AjaxReturnViewModel<boolean>;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    async DenyRx({ commit }, payload: { questID: number; questAffiliateID: number; visitPerformed: boolean; physicianComments: string; soapNote: string; approveQPRIDs: Array<QuestionnaireProductRxsDetails> }) {
      try {
        const pVM = await api.post(`/api/Physician/DenyRx`, payload);
        return pVM.data as AjaxReturnViewModel<boolean>;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    async DenyRefill({ commit }, payload: { questID: number; questAffiliateID: number; physicianComments: string; soapNote: string }) {
      try {
        const pVM = await api.post(`/api/Physician/DenyRefill?questID=${payload.questID}&questAffiliateID=${payload.questAffiliateID}&physicianComments=${payload.physicianComments}&soapNote=${payload.soapNote}`);
        return pVM.data as AjaxReturnViewModel<boolean>;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    async MarkMessageSent({ commit }, payload: { questID: number; questAffiliateID: number; soapNote: string }) {
      try {
        const pVM = await api.post(`/api/Physician/MarkMessageSent?questID=${payload.questID}&questAffiliateID=${payload.questAffiliateID}&soapNote=${payload.soapNote}`);
        return pVM.data as AjaxReturnViewModel<boolean>;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    async SendVisitReminder({ commit }, payload: { questionnaireID: number; affiliateID: number }) {
      try {
        const pVM = await api.post(`/api/Physician/SendVisitReminder?questionnaireID=${payload.questionnaireID}&affiliateID=${payload.affiliateID}`);
        return pVM.data as AjaxReturnViewModel<boolean>;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    async GetPatientProfile({ commit, state }, payload: { patientUserID: number; patientAffiliateID: number }) {
      try {
        let getUrl = "/api/Home/GetPatientProfile";
        if (state.Role == "Admin") getUrl = `/api/Admin/GetPatientProfile?patientUserID=${payload.patientUserID}&patientAffiliateID=${payload.patientAffiliateID}`;
        if (state.Role == "AffiliateAdmin") getUrl = `/api/affiliateadmin/GetPatientProfile?patientUserID=${payload.patientUserID}&patientAffiliateID=${payload.patientAffiliateID}`;
        if (state.Role == "Support") getUrl = `/api/Support/GetPatientProfile?patientUserID=${payload.patientUserID}&patientAffiliateID=${payload.patientAffiliateID}`;
        if (state.Role == "Pharmacy") getUrl = `/api/Pharmacy/GetPatientProfile?patientUserID=${payload.patientUserID}&patientAffiliateID=${payload.patientAffiliateID}`;
        const pVM = await api.get(getUrl);
        return pVM.data;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    async SavePatientProfile({ commit, state }, payload: PatientViewModel) {
      try {
        let postUrl = "/api/Home/SavePatientProfile";
        if (state.Role == "Admin") postUrl = `/api/Admin/SavePatientProfile`;
        if (state.Role == "Support") postUrl = `/api/Support/SavePatientProfile`;
        if (state.Role == "Pharmacy") postUrl = `/api/Pharmacy/SavePatientProfile`;
        if (state.Role == "AffiliateAdmin") postUrl = `/api/AffiliateAdmin/SavePatientProfile`;
        const pVM = await api.post(postUrl, payload);
        return pVM.data as AjaxReturnViewModel<boolean>;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    async ToggleSubscription({ commit, state }, t: number) {
      try {
        const pVM = await api.post(`/api/Home/ToggleSubscription?t=${t}`);
        return pVM.data as AjaxReturnViewModel<boolean>;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    async Renew({ commit, state }, t: number) {
      try {
        const pVM = await api.post(`/api/Home/Renew?t=${t}`);
        return pVM.data as AjaxReturnViewModel<boolean>;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    async LookupCustomer({ commit, state }, payload: { firstName: string; lastName: string; userID: string; email: string; dateOfBirth: number }) {
      try {
        let postUrl = "/api/Pharmacy/LookupCustomer";
        if (state.Role == "Support") postUrl = `/api/Support/LookupCustomer`;
        if (state.Role == "AffiliateAdmin") postUrl = `/api/affiliateadmin/LookupCustomer`;
        const dob = payload.dateOfBirth ? payload.dateOfBirth.toString() : "";
        const email = payload.email ? payload.email.toString() : "";
        const lastName = payload.lastName ? payload.lastName.toString() : "";
        const firstName = payload.firstName ? payload.firstName.toString() : "";
        const userID = payload.userID ? payload.userID.toString() : "";
        const pVM = await api.post(`${postUrl}?firstName=${firstName}&lastName=${lastName}&userID=${userID}&email=${email}&dateOfBirth=${dob}`);
        if (pVM.data) commit("setCustomerLookup", pVM.data);
        return pVM.data as AjaxReturnViewModel<LookupPatientAsSupportViewModel[]>;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    async LookupPatient({ commit, state }, payload: { firstName: string; lastName: string; userID: string; email: string; dateOfBirth: number }) {
      try {
        const postUrl = "/api/physician/LookupPatient";
        const firstName = payload.firstName ? payload.firstName.toString() : "";
        const lastName = payload.lastName ? payload.lastName.toString() : "";
        const userID = payload.userID ? payload.userID.toString() : "";
        const email = payload.email ? payload.email.toString() : "";
        const dob = payload.dateOfBirth ? payload.dateOfBirth.toString() : "";

        const pVM = await api.post(`${postUrl}?firstName=${firstName}&lastName=${lastName}&userID=${userID}&email=${email}&dateOfBirth=${dob}`);
        return pVM.data as AjaxReturnViewModel<LookupPatientAsPhysicianViewModel[]>;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    async GetCustomerDetail({ commit, state }, payload: { lastName: string; userID: string; email: string; dateOfBirth: number; id: number }) {
      console.log("GetCustomerDetail");
      try {
        let getUrl = "/api/Pharmacy/GetCustomerDetail";
        if (state.Role == "Support") getUrl = `/api/Support/GetCustomerDetail`;
        if (state.Role == "AffiliateAdmin") getUrl = `/api/affiliateadmin/GetCustomerDetail`;
        const dob = payload.dateOfBirth ? payload.dateOfBirth.toString() : "";
        const pVM = await api.get(`${getUrl}?lastName=${payload.lastName}&email=${payload.email}&dateOfBirth=${dob}&id=${payload.id}`);
        return pVM.data as AjaxReturnViewModel<CustomerDetailViewModel>;
      } catch (e) {
        console.log(e);
        return null;
      }
    },

    async GetPatientNotes({ commit }, payload: { patientUserID: number; noteType: number | number[]; patientAffiliateID: number }) {
      try {
        // Construct query parameters
        const params = new URLSearchParams();
        params.append("patientUserID", payload.patientUserID.toString());
        params.append("patientAffiliateID", payload.patientAffiliateID.toString());

        if (Array.isArray(payload.noteType)) {
          // If noteType is an array, append each value separately
          payload.noteType.forEach((nt) => params.append("noteType", nt.toString()));
        } else {
          // If noteType is a single number, append it once
          params.append("noteType", payload.noteType.toString());
        }

        // Make API request with constructed query parameters
        const pVM = await api.get(`/api/PatientNotes/GetPatientNotes?${params.toString()}`);

        return pVM.data as AjaxReturnViewModel<PatientNote[]>;
      } catch (e) {
        console.log(e);
        return null;
      }
    },

    async SavePatientNote({ commit }, payload: { id: number; note: string; noteType: number; patientUserID: number; patientAffiliateID: number }) {
      try {
        const pVM = await api.post(`/api/PatientNotes/SavePatientNote?id=${payload.id}&note=${payload.note}&noteType=${payload.noteType}&patientUserID=${payload.patientUserID}&patientaffiliateID=${payload.patientAffiliateID}`);
        return pVM.data as AjaxReturnViewModel<boolean>;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    async ChangeUserName({ commit, state }, payload: { patientUserID: number; patientAffiliateID: number; email: string }) {
      try {
        let postUrl = "";
        if (state.Role == "Support") postUrl = `/api/Support/ChangeUserName?patientUserID=${payload.patientUserID}&patientAffiliateID=${payload.patientAffiliateID}&email=${payload.email}`;
        if (state.Role == "AffiliateAdmin") postUrl = `/api/affiliateadmin/ChangeUserName?patientUserID=${payload.patientUserID}&patientAffiliateID=${payload.patientAffiliateID}&email=${payload.email}`;
        const pVM = await api.post(postUrl);
        return pVM.data as AjaxReturnViewModel<CustomerDetailViewModel>;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    async GetOrderHistory({ commit, state }, payload: { patientUserID: number; patientAffiliateID: number; questionnaireTypeID: number; rxID: number }) {
      try {
        let getUrl = `/api/Home/GetOrderHistory?questionnaireTypeID=${payload.questionnaireTypeID}&rxID=${payload.rxID}`;
        if (state.Role == "Admin") getUrl = `/api/Admin/GetOrderHistory?patientUserID=${payload.patientUserID}&patientAffiliateID=${payload.patientAffiliateID}&questionnaireTypeID=${payload.questionnaireTypeID}&rxID=${payload.rxID}`;
        if (state.Role == "AffiliateAdmin") getUrl = `/api/affiliateadmin/GetOrderHistory?patientUserID=${payload.patientUserID}&patientAffiliateID=${payload.patientAffiliateID}&questionnaireTypeID=${payload.questionnaireTypeID}&rxID=${payload.rxID}`;
        if (state.Role == "Support") getUrl = `/api/Support/GetOrderHistory?patientUserID=${payload.patientUserID}&patientAffiliateID=${payload.patientAffiliateID}&questionnaireTypeID=${payload.questionnaireTypeID}&rxID=${payload.rxID}`;
        if (state.Role == "Pharmacy") getUrl = `/api/Pharmacy/GetOrderHistory?patientUserID=${payload.patientUserID}&patientAffiliateID=${payload.patientAffiliateID}&questionnaireTypeID=${payload.questionnaireTypeID}&rxID=${payload.rxID}`;
        const pVM = await api.get(getUrl);
        return pVM.data as AjaxReturnViewModel<OrderViewModel[]>;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    async SaveRx({ commit }, rx: Rx) {
      try {
        const pVM = await api.post("/api/Pharmacy/SaveRx", rx);
        return pVM.data as AjaxReturnViewModel<boolean>;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    async GetShippingCarriers({ commit }) {
      try {
        const response = await api.get(`/api/${this.state.Role}/GetShippingCarriers`);
        if (response && response.data && response.data.success) {
          commit("setShippingCarriers", response.data.data);
        }
        return response.data ? response.data : null;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    async GetPharmShoppingCart({ commit }, id): Promise<AjaxReturnViewModel<ShoppingCartViewModel> | null> {
      try {
        const cart = await api.get("/api/pharmacy/GetShoppingCart?id=" + id);
        return cart.data as AjaxReturnViewModel<ShoppingCartViewModel>;
      } catch (e) {
        console.log(e);
      }
      return null;
    },
    async SaveShoppingCartTrackingNumber({ commit }, data): Promise<AjaxReturnViewModel<boolean> | null> {
      try {
        const cart = await api.post("/api/pharmacy/UpdateOTCTracking", data);
        return cart.data as AjaxReturnViewModel<boolean>;
      } catch (e) {
        console.log(e);
      }
      return null;
    },
    async SaveTrackingNumber({ commit }, payload: { fillIdentifier: string; shippingCarrierID: number; trackingNumber: string; notifyCustomer: boolean }) {
      try {
        if (this.state.Role === "Pharmacy") {
          const pVM = await api.post(`/api/Pharmacy/SaveTrackingNumber?fillIdentifier=${payload.fillIdentifier}&shippingCarrierID=${payload.shippingCarrierID}&trackingNumber=${payload.trackingNumber}`);
          return pVM.data as AjaxReturnViewModel<boolean>;
        }

        if (this.state.Role === "Support") {
          const pVM = await api.post(`/api/Support/SaveTrackingNumber?RxID=${payload.fillIdentifier}&shippingCarrierID=${payload.shippingCarrierID}&trackingNumber=${payload.trackingNumber}&notify=${payload.notifyCustomer}`);
          return pVM.data as AjaxReturnViewModel<boolean>;
        }
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    async UserEmailConfirmation({ commit, state }, payload: { code: string }): Promise<AjaxReturnViewModel<boolean> | null> {
      try {
        const resp = await api.post("/api/Home/UserEmailConfirmation", payload);
        if (resp) {
          return resp.data;
        }
      } catch (e) {
        console.log(e);
      }
      return null;
    },
    async NewUserInvite({ commit, state }, payload: { code: string }): Promise<AjaxReturnViewModel<boolean> | null> {
      try {
        const resp = await api.post("/api/Home/NewUserInvite", payload);
        if (resp) {
          return resp.data;
        }
      } catch (e) {
        console.log(e);
      }
      return null;
    },
    async UserForgotPassword({ commit, state }, payload: { username: string; token: string }): Promise<AjaxReturnViewModel<string> | null> {
      try {
        const resp = await api.post(`/api/Home/UserForgotPassword`, { username: payload.username, token: payload.token });
        if (resp) {
          return resp.data;
        }
      } catch (e) {
        console.log(e);
      }
      return null;
    },
    async UserPasswordReset({ commit, state }, payload: { code: string; newPassword: string }): Promise<AjaxReturnViewModel<boolean> | null> {
      try {
        const resp = await api.post("/api/Home/UserPasswordReset", { newPassword: payload.newPassword, code: payload.code }, { headers: { Authorization: `Bearer ${payload.code}` } });
        if (resp) {
          return resp.data;
        }
      } catch (e) {
        console.log(e);
      }
      return null;
    },
    async NewUserPasswordReset({ commit, state }, payload: { code: string; newPassword: string }): Promise<AjaxReturnViewModel<boolean> | null> {
      try {
        const resp = await api.post("/api/Home/NewUserPasswordReset", { newPassword: payload.newPassword, code: payload.code }, { headers: { Authorization: `Bearer ${payload.code}` } });
        if (resp) {
          return resp.data;
        }
      } catch (e) {
        console.log(e);
      }
      return null;
    },
    async UserIs2FA({ commit }, code: string) {
      try {
        const response = await axios.post(`/api/Home/UserIs2FA?code=${code}`);
        return response?.data?.data;
      } catch (e) {
        return null;
      }
    },
    async UpdateUTM({ commit }, utm) {
      try {
        const response = await api.post("/api/Home/UpdateUTM", utm);
        if (response?.data?.data) {
          commit("setUTM", null);
          localStorage.removeItem("utm_source");
          localStorage.removeItem("utm_medium");
          localStorage.removeItem("utm_campaign");
          localStorage.removeItem("utm_term");
          localStorage.removeItem("utm_content");
          localStorage.removeItem("gclid");
          localStorage.removeItem("fbclid");
        }
        return response.data as AjaxReturnViewModel<boolean>;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    async GetPharmacySchedule({ commit, state }) {
      try {
        const resp = await api.get("/api/Pharmacy/GetPharmacySchedule");
        if (resp && resp.data && resp.data.success) {
          return resp.data.data;
        }
      } catch (e) {
        console.log(e);
      }
      return null;
    },
    async UpdatePharmacySchedule({ commit }, ps: PharmacySchedule[]) {
      try {
        const pVM = await api.post("/api/Pharmacy/UpdatePharmacySchedule", ps);
        return pVM.data as AjaxReturnViewModel<boolean>;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    async GetPharmacySpecialHours({ commit, state }) {
      try {
        const resp = await api.get("/api/pharmacy/GetSpecialHours");
        if (resp && resp.data && resp.data.success) {
          return resp.data.data;
        }
      } catch (e) {
        console.log(e);
      }
      return null;
    },
    async UpdatePharmacySpecialHours({ commit }, ps: PharmacySpecialHours[]) {
      try {
        const pVM = await api.post("/api/Pharmacy/UpdateSpecialHours", ps);
        return pVM.data as AjaxReturnViewModel<boolean>;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    async GetUsersForAffiliateAdmin({ commit }, payload) {
      try {
        const resp = await api.post("/api/affiliateadmin/GetUsersForAffiliateAdmin", payload);
        if (resp && resp.data && resp.data.success) {
          return resp.data as AjaxReturnViewModel<UsersForAdminViewModel[]>;
        }
      } catch (e) {
        console.log(e);
      }
    },
    async GetUsersForAdmin({ commit }, payload) {
      try {
        const resp = await api.post("/api/Admin/GetUsers", payload);
        if (resp && resp.data && resp.data.success) {
          return resp.data as AjaxReturnViewModel<UsersForAdminViewModel[]>;
        }
      } catch (e) {
        console.log(e);
      }
    },

    async GetUsersForUserIDReportParam({ commit, state }, { affiliateID, query }) {
      let postUrl = "";
      if (state.Role === "Admin") postUrl = `/api/Admin/GetUsersForUserIDReportParam`;
      if (state.Role === "AffiliateAdmin") postUrl = `/api/AffiliateAdmin/GetUsersForUserIDReportParam`;
      try {
        const resp = await api.post(postUrl, null, {
          params: { affiliateID, query },
        });
        if (resp && resp.data && resp.data.success) {
          return resp.data as AjaxReturnViewModel<UsersForAdminViewModel[]>;
        }
      } catch (e) {
        console.log(e);
      }
    },

    async GetPromotionData({ state }, { AffiliateID, Name, Code }) {
      try {
        let endpoint = "";
        if (state.Role === "Admin") {
          endpoint = "/api/admin/GetPromotionData";
        } else if (state.Role === "AffiliateAdmin") {
          endpoint = "/api/affiliateadmin/GetPromotionData";
        }

        const resp = await api.get(endpoint, {
          params: {
            AffiliateID,
            Name,
            Code,
          },
        });
        if (resp && resp.data && resp.data.success) {
          return resp.data.data;
        } else {
          throw new Error("Failed to get promotions");
        }
      } catch (e) {
        console.error(e);
        return null;
      }
    },
    async GetAffiliateProgramsAndProduct({ commit, state }, { affiliateID }) {
      try {
        let endpoint = "";

        if (state.Role === "Admin") {
          endpoint = "/api/admin/GetAffiliateProgramsAndProduct";
        } else if (state.Role === "AffiliateAdmin") {
          endpoint = "/api/affiliateadmin/GetAffiliateProgramsAndProduct";
        } else if (state.Role === "API") {
          endpoint = "/api/apirole/GetAffiliateProgramsAndProduct";
        } else if (state.Role === "Pharmacy") {
          endpoint = "/api/pharmacy/GetAffiliateProgramsAndProduct";
        } else if (state.Role === "Physician") {
          endpoint = "/api/physician/GetAffiliateProgramsAndProduct";
        } else if (state.Role === "Support") {
          endpoint = "/api/support/GetAffiliateProgramsAndProduct";
        }

        if (!endpoint) {
          throw new Error(`No endpoint defined for role: ${state.Role}`);
        }
        const resp = await api.get(`${endpoint}/${affiliateID}`);
        if (resp && resp.data && resp.data.success) {
          return resp.data.data;
        }
      } catch (e) {
        console.log(e);
      }
      return null;
    },
    async GetAffiliateNames({ commit, state }) {
      try {
        let endpoint = "";

        if (state.Role === "Admin") endpoint = "/api/admin/GetAffiliateNames";
        else if (state.Role === "AffiliateAdmin") endpoint = "/api/affiliateadmin/GetAffiliateNames";
        else if (state.Role === "Pharmacy") endpoint = "/api/pharmacy/GetAffiliateNames";
        else if (state.Role === "Support") endpoint = "/api/support/GetAffiliateNames";
        else if (state.Role === "Physician") endpoint = "/api/physician/GetAffiliateNames";
        else if (state.Role === "API") endpoint = "/api/apirole/GetAffiliateNames";

        if (!endpoint) {
          throw new Error(`No endpoint defined for role: ${state.Role}`);
        }

        const resp = await api.get(endpoint);
        if (resp && resp.data) {
          const affiliateNames = Object.entries(resp.data).map(([key, value]) => ({ id: key, name: value }));
          return affiliateNames;
        }
      } catch (e) {
        console.log(e);
      }
      return null;
    },
    async GetAllReportNames({ commit, state }) {
      try {
        let endpoint = "";

        if (state.Role === "Admin") endpoint = "/api/admin/GetAllReportNames";

        if (!endpoint) {
          throw new Error(`No endpoint defined for role: ${state.Role}`);
        }

        const resp = await api.get(endpoint);
        if (resp && resp.data) {
          const reportNames = Object.entries(resp.data).map(([key, value]) => ({ id: key, name: value }));
          return reportNames;
        }
      } catch (e) {
        console.log(e);
      }
      return null;
    },
    async GetAllAffiliateReportNames({ commit, state }) {
      try {
        let endpoint = "";

        if (state.Role === "Admin") endpoint = "/api/admin/GetAllAffiliateReportNames";
        else if (state.Role === "AffiliateAdmin") endpoint = "/api/affiliateadmin/GetAllAffiliateReportNames";

        if (!endpoint) {
          throw new Error(`No endpoint defined for role: ${state.Role}`);
        }

        const resp = await api.get(endpoint);
        if (resp && resp.data) {
          return resp.data.data;
        }
      } catch (e) {
        console.log(e);
      }
      return null;
    },
    async GetAffiliatePharmacyNames({ commit, state }) {
      try {
        const resp = await api.get("/api/affiliateadmin/GetPharmacyNames");
        if (resp && resp.data) {
          const pharmacyNames = Object.entries(resp.data).map(([key, value]) => ({ id: key, name: value }));
          return pharmacyNames;
        }
      } catch (e) {
        console.log(e);
      }
      return null;
    },
    async GetPharmacyNames({ commit, state }) {
      try {
        const resp = await api.get("/api/admin/GetPharmacyNames");
        if (resp && resp.data) {
          const pharmacyNames = Object.entries(resp.data).map(([key, value]) => ({ id: key, name: value }));
          return pharmacyNames;
        }
      } catch (e) {
        console.log(e);
      }
      return null;
    },
    async GetAffiliateVitaminSupplierNames({ commit, state }) {
      try {
        const resp = await api.get("/api/affiliateadmin/GetVitaminSupplierNames");
        if (resp && resp.data) {
          const viteSupNames = Object.entries(resp.data).map(([key, value]) => ({ id: key, name: value }));
          return viteSupNames;
        }
      } catch (e) {
        console.log(e);
      }
      return null;
    },
    async GetVitaminSupplierNames({ commit, state }) {
      try {
        const resp = await api.get("/api/admin/GetVitaminSupplierNames");
        if (resp && resp.data) {
          const viteSupNames = Object.entries(resp.data).map(([key, value]) => ({ id: key, name: value }));
          return viteSupNames;
        }
      } catch (e) {
        console.log(e);
      }
      return null;
    },
    async GetAffiliateQuestionnaireTypes({ commit, state }) {
      try {
        let postUrl = "";
        if (state.Role == "AffiliateAdmin") postUrl = `/api/affiliateadmin/GetQuestionnaireTypes`;
        if (state.Role == "Admin") postUrl = `/api/Admin/GetQuestionnaireTypes`;
        if (state.Role == "Support") postUrl = `/api/Support/GetQuestionnaireTypes`;
        const resp = await api.get(postUrl);
        if (resp && resp.data) {
          const questTypes = Object.entries(resp.data).map(([key, value]) => ({ id: key, name: value }));
          return questTypes;
        }
      } catch (e) {
        console.log(e);
      }
      return null;
    },
    async GetQuestionnaireTypes({ commit, state }) {
      try {
        const resp = await api.get("/api/admin/GetQuestionnaireTypes");
        if (resp && resp.data) {
          const questTypes = Object.entries(resp.data).map(([key, value]) => ({ id: key, name: value }));
          return questTypes;
        }
      } catch (e) {
        console.log(e);
      }
      return null;
    },
    async GetAllAffiliateQuestionnaireTypes({ commit, state }) {
      try {
        const resp = await api.get("/api/admin/GetAffiliateQuestionnaireTypes");
        if (resp && resp.data) {
          return resp.data;
        }
      } catch (e) {
        console.log(e);
      }
    },
    async AffiliateAdminCreateAdminUser({ commit }, payload) {
      try {
        const pVM = await api.post("/api/affiliateadmin/AffiliateAdminCreateAdminUser", payload);
        return pVM.data as AjaxReturnViewModel<boolean>;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    async CreateAdminUser({ commit }, payload) {
      try {
        const pVM = await api.post("/api/admin/CreateAdminUser", payload);
        return pVM.data as AjaxReturnViewModel<boolean>;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    async CreateAAAffiliateAdminUser({ commit }, au: AdminUserViewModel) {
      try {
        const pVM = await api.post("/api/affiliateadmin/CreateAffiliateAdminUser", au);
        return pVM.data as AjaxReturnViewModel<boolean>;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    async CreateAffiliateAdminUser({ commit }, au: AdminUserViewModel) {
      try {
        const pVM = await api.post("/api/admin/CreateAffiliateAdminUser", au);
        return pVM.data as AjaxReturnViewModel<boolean>;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    async CreateAAAPIUser({ commit }, au: AdminUserViewModel) {
      try {
        const pVM = await api.post("/api/affiliateadmin/CreateAPIUser", au);
        return pVM.data as AjaxReturnViewModel<boolean>;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    async CreateAPIUser({ commit }, au: AdminUserViewModel) {
      try {
        const pVM = await api.post("/api/admin/CreateAPIUser", au);
        return pVM.data as AjaxReturnViewModel<boolean>;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    async CreatePharmacyUser({ commit }, payload) {
      try {
        const pVM = await api.post("/api/admin/CreatePharmacyUser", payload);
        return pVM.data as AjaxReturnViewModel<boolean>;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    async CreatePhysicianUser({ commit }, payload) {
      try {
        const pVM = await api.post("/api/admin/CreatePhysicianUser", payload);
        return pVM.data as AjaxReturnViewModel<boolean>;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    async CreateVitaminSupplierUser({ commit }, au: AdminUserViewModel) {
      try {
        const pVM = await api.post("/api/admin/CreateVitaminSupplierUser", au);
        return pVM.data as AjaxReturnViewModel<boolean>;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    async UserPasswordChange({ commit, state }, payload: { p: string; np: string; c: string; t: string; m: TwoFactorTypeEnum }): Promise<AjaxReturnViewModel<boolean> | null> {
      try {
        const resp = await api.post("/api/members/changePassword", payload);
        if (resp) {
          return resp.data;
        }
      } catch (e) {
        console.log(e);
      }
      return null;
    },

    async AdminPasswordChange({ commit, state }, payload: { np: string; patientUserID: number; patientAffiliateID: number }) {
      try {
        let postUrl = "";
        if (state.Role === "Support") postUrl = `/api/support/AdminPasswordChange`;
        if (state.Role === "AffiliateAdmin") postUrl = `/api/affiliateadmin/AdminPasswordChange`;
        if (state.Role === "Pharmacy") postUrl = `/api/pharmacy/AdminPasswordChange`;

        const r = await api.post(postUrl, payload);
        return r.data;
      } catch (e) {
        return null;
      }
    },
    async UpdateAAEmail({ commit }, vm: AdminEmailUpdateViewModel) {
      try {
        const pVM = await api.post("/api/affiliateadmin/UpdateAAEmail", vm);
        return pVM.data as AjaxReturnViewModel<boolean>;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    async UpdateEmail({ commit }, vm: AdminEmailUpdateViewModel) {
      try {
        const pVM = await api.post("/api/admin/UpdateEmail", vm);
        return pVM.data as AjaxReturnViewModel<boolean>;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    async UpdateAAAdminUser({ commit }, auvm: AdminUserViewModel[]) {
      try {
        const pVM = await api.post("/api/affiliateadmin/UpdateAAAdminUser", auvm);
        return pVM.data as AjaxReturnViewModel<boolean>;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    async UpdateAdminUser({ commit }, auvm: AdminUserViewModel[]) {
      try {
        const pVM = await api.post("/api/admin/UpdateAdminUser", auvm);
        return pVM.data as AjaxReturnViewModel<boolean>;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    async UpdateAAAffiliateAdminUser({ commit }, auvm: AdminUserViewModel[]) {
      try {
        const pVM = await api.post("/api/affiliateadmin/UpdateAAAffiliateAdminUser", auvm);
        return pVM.data as AjaxReturnViewModel<boolean>;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    async UpdateAffiliateAdminUser({ commit }, auvm: AdminUserViewModel[]) {
      try {
        const pVM = await api.post("/api/admin/UpdateAffiliateAdminUser", auvm);
        return pVM.data as AjaxReturnViewModel<boolean>;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    async UpdateAAAPIUser({ commit }, auvm: AdminUserViewModel[]) {
      try {
        const pVM = await api.post("/api/affiliateadmin/UpdateAAAPIUser", auvm);
        return pVM.data as AjaxReturnViewModel<boolean>;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    async UpdateAPIUser({ commit }, auvm: AdminUserViewModel[]) {
      try {
        const pVM = await api.post("/api/admin/UpdateAPIUser", auvm);
        return pVM.data as AjaxReturnViewModel<boolean>;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    async UpdatePatientUser({ commit }, auvm: AdminUserViewModel[]) {
      try {
        const pVM = await api.post("/api/admin/UpdatePatientUser", auvm);
        return pVM.data as AjaxReturnViewModel<boolean>;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    async UpdatePharmacyUser({ commit }, auvm: AdminUserViewModel[]) {
      try {
        const pVM = await api.post("/api/admin/UpdatePharmacyUser", auvm);
        return pVM.data as AjaxReturnViewModel<boolean>;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    async UpdateVitaminSupplierUser({ commit }, auvm: AdminUserViewModel[]) {
      try {
        const pVM = await api.post("/api/admin/UpdateVitaminSupplierUser", auvm);
        return pVM.data as AjaxReturnViewModel<boolean>;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    async UpdatePhysicianUser({ commit }, auvm: AdminUserViewModel[]) {
      try {
        const pVM = await api.post("/api/admin/UpdatePhysicianUser", auvm);
        return pVM.data as AjaxReturnViewModel<boolean>;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    async GetPhysicianState({ commit, state }, userID) {
      try {
        const resp = await api.get(`/api/admin/GetPhysicianState/${userID}`);
        if (resp && resp.data && resp.data.success) {
          return resp.data.data;
        }
      } catch (e) {
        console.log(e);
      }
      return null;
    },
    async GetAAUserAccess({ commit, state }, userID) {
      try {
        const resp = await api.get(`/api/affiliateadmin/GetAAUserAccess/${userID}`);
        if (resp && resp.data && resp.data.success) {
          return resp.data.data;
        }
      } catch (e) {
        console.log(e);
      }
      return null;
    },
    async GetUserAccess({ commit, state }, userID) {
      try {
        const resp = await api.get(`/api/admin/GetUserAccess/${userID}`);
        if (resp && resp.data && resp.data.success) {
          return resp.data.data;
        }
      } catch (e) {
        console.log(e);
      }
      return null;
    },
    async GetAAUserReportAccess({ commit, state }, userID) {
      try {
        const resp = await api.get(`/api/affiliateadmin/GetAAUserReportAccess/${userID}`);
        if (resp && resp.data && resp.data.success) {
          return resp.data.data;
        }
      } catch (e) {
        console.log(e);
      }
      return null;
    },
    async GetUserReportAccess({ commit, state }, userID) {
      try {
        const resp = await api.get(`/api/admin/GetUserReportAccess/${userID}`);
        if (resp && resp.data && resp.data.success) {
          return resp.data.data;
        }
      } catch (e) {
        console.log(e);
      }
      return null;
    },
    async GetPhysicianData({ commit, state }, userID) {
      try {
        const resp = await api.get(`/api/admin/GetPhysicianData/${userID}`);
        if (resp && resp.data && resp.data.success) {
          return resp.data.data;
        }
      } catch (e) {
        console.log(e);
      }
      return null;
    },
    async CreatePromotion({ commit, state }, { auvm }: { auvm: NewPromotionViewModel[] }) {
      try {
        let endpoint = "";

        if (state.Role === "Admin") {
          endpoint = "/api/admin/CreatePromotion";
        } else if (state.Role === "AffiliateAdmin") {
          endpoint = "/api/affiliateadmin/CreatePromotion";
        }

        if (!endpoint) {
          throw new Error(`No endpoint defined for role: ${state.Role}`);
        }

        const pVM = await api.post(endpoint, auvm);
        return pVM.data as AjaxReturnViewModel<boolean>;
      } catch (e) {
        console.log(e);
      }
      return null;
    },
    async UpdatePromotion({ commit, state }, { auvm }: { auvm: PromotionRx[] }) {
      try {
        let endpoint = "";

        if (state.Role === "Admin") {
          endpoint = "/api/admin/UpdatePromotion";
        } else if (state.Role === "AffiliateAdmin") {
          endpoint = "/api/affiliateadmin/UpdatePromotion";
        }

        const pVM = await api.post(endpoint, auvm);
        return pVM.data as AjaxReturnViewModel<boolean>;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    async UpdatePromotionStatus({ commit, state }, { statusUpdate }) {
      try {
        let endpoint = "";

        if (state.Role === "Admin") {
          endpoint = "/api/admin/UpdatePromotionStatus";
        } else if (state.Role === "AffiliateAdmin") {
          endpoint = "/api/affiliateadmin/UpdatePromotionStatus";
        }

        const response = await api.post(endpoint, statusUpdate);
        return response.data as AjaxReturnViewModel<boolean>;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    async GetReportNames({ commit, state }) {
      try {
        let endpoint = "";

        if (state.Role === "Admin") endpoint = "/api/admin/GetReportNames";
        else if (state.Role === "AffiliateAdmin") endpoint = "/api/affiliateadmin/GetReportNames";
        else if (state.Role === "Pharmacy") endpoint = "/api/pharmacy/GetReportNames";
        else if (state.Role === "Support") endpoint = "/api/support/GetReportNames";
        else if (state.Role === "Physician") endpoint = "/api/physician/GetReportNames";
        else if (state.Role === "API") endpoint = "/api/apirole/GetReportNames";

        if (!endpoint) {
          throw new Error(`No endpoint defined for role: ${state.Role}`);
        }

        const resp = await api.get(endpoint);
        if (resp && resp.data) {
          return resp.data;
        }
      } catch (e) {
        console.log(e);
      }
      return null;
    },
    async GetReportParameters({ commit, state }, { reportId }) {
      try {
        let endpoint = "";

        if (state.Role === "Admin") endpoint = "/api/admin/GetReportParameters";
        else if (state.Role === "AffiliateAdmin") endpoint = "/api/affiliateadmin/GetReportParameters";
        else if (state.Role === "Pharmacy") endpoint = "/api/pharmacy/GetReportParameters";
        else if (state.Role === "Support") endpoint = "/api/support/GetReportParameters";
        else if (state.Role === "Physician") endpoint = "/api/physician/GetReportParameters";
        else if (state.Role === "API") endpoint = "/api/apirole/GetReportParameters";

        if (!endpoint) {
          throw new Error(`No endpoint defined for role: ${state.Role}`);
        }

        const resp = await api.get(`${endpoint}/${reportId}`);

        if (resp && resp.data && resp.data.success) {
          return resp.data.data;
        }
      } catch (e) {
        console.log(e);
      }
      return null;
    },
    async SubmitReportData({ commit, state }, { submissionData }) {
      try {
        let endpoint = "";

        if (state.Role === "Admin") endpoint = "/api/admin/SubmitReportData";
        else if (state.Role === "AffiliateAdmin") endpoint = "/api/affiliateadmin/SubmitReportData";
        else if (state.Role === "Pharmacy") endpoint = "/api/pharmacy/SubmitReportData";
        else if (state.Role === "Support") endpoint = "/api/support/SubmitReportData";
        else if (state.Role === "Physician") endpoint = "/api/physician/SubmitReportData";
        else if (state.Role === "API") endpoint = "/api/apirole/SubmitReportData";

        if (!endpoint) {
          throw new Error(`No endpoint defined for role: ${state.Role}`);
        }

        const resp = await api.post(endpoint, submissionData);
        if (resp && resp.data && resp.data.success) {
          return resp.data as AjaxReturnViewModel<ReportDataViewModel>;
        }
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    async GetChartData({ commit, state }) {
      try {
        const resp = await api.get("/api/affiliateadmin/GetChartData");
        if (resp && resp.data) {
          return resp.data;
        }
      } catch (e) {
        console.log(e);
      }
      return null;
    },
    async GetUnreadMessages({ commit, state }) {
      try {
        let endpoint = "";

        if (state.Role === "Pharmacy") endpoint = "/api/pharmacy/GetUnreadMessages";
        else if (state.Role === "Support") endpoint = "/api/support/GetUnreadMessages";

        if (!endpoint) {
          throw new Error(`No endpoint defined for role: ${state.Role}`);
        }

        const resp = await api.post(endpoint);
        if (resp && resp.data && resp.data.success) {
          return resp.data;
        }
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    async GetTotalOrdersShipped({ commit, state }) {
      try {
        const resp = await api.post("/api/pharmacy/GetTotalOrdersShipped");
        if (resp && resp.data) {
          return resp.data;
        }
      } catch (e) {
        console.log(e);
      }
      return null;
    },
    async CancelCharge({ commit, state }, payload: { patientUserID: number; patientAffiliateID: number; subscriptionID: number; paymentTransactionType: string }) {
      try {
        let postUrl = "";
        if (state.Role == "Support") postUrl = `/api/support/CancelScheduledCharge?patientUserID=${payload.patientUserID}&patientAffiliateID=${payload.patientAffiliateID}&subscriptionID=${payload.subscriptionID}&paymentTransactionType=${payload.paymentTransactionType}`;
        if (state.Role == "AffiliateAdmin") postUrl = `/api/affiliateadmin/CancelScheduledCharge?patientUserID=${payload.patientUserID}&patientAffiliateID=${payload.patientAffiliateID}&subscriptionID=${payload.subscriptionID}&paymentTransactionType=${payload.paymentTransactionType}`;
        const pVM = await api.post(postUrl);
        return pVM.data as AjaxReturnViewModel<boolean>;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    async UpdateScheduledCharge({ commit, state }, payload: { patientUserID: number; patientAffiliateID: number; subscriptionID: number; changeChargeAmount: number; changeChargeNote: string; paymentTransactionType: string }) {
      try {
        let postUrl = "";
        if (state.Role == "Support") postUrl = `/api/support/UpdateScheduledCharge?patientUserID=${payload.patientUserID}&patientAffiliateID=${payload.patientAffiliateID}&subscriptionID=${payload.subscriptionID}&changeChargeAmount=${payload.changeChargeAmount}&changeChargeNote=${payload.changeChargeNote}&paymentTransactionType=${payload.paymentTransactionType}`;
        if (state.Role == "AffiliateAdmin") postUrl = `/api/affiliateadmin/UpdateScheduledCharge?patientUserID=${payload.patientUserID}&patientAffiliateID=${payload.patientAffiliateID}&subscriptionID=${payload.subscriptionID}&changeChargeAmount=${payload.changeChargeAmount}&changeChargeNote=${payload.changeChargeNote}&paymentTransactionType=${payload.paymentTransactionType}`;
        const pVM = await api.post(postUrl);
        return pVM.data as AjaxReturnViewModel<boolean>;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    async RefundCharge({ commit, state }, payload: { patientUserID: number; patientAffiliateID: number; questionnaireID: number; transactionID: string; refundAmount: number; paymentTransactionType: string; refundNote: string }) {
      try {
        let postUrl = "";
        if (state.Role == "Support") postUrl = `/api/support/RefundCharge?patientUserID=${payload.patientUserID}&patientAffiliateID=${payload.patientAffiliateID}&questionnaireID=${payload.questionnaireID}&transactionID=${payload.transactionID}&refundAmount=${payload.refundAmount}&paymentTransactionType=${payload.paymentTransactionType}&refundNote=${payload.refundNote}`;
        if (state.Role == "AffiliateAdmin")
          postUrl = `/api/affiliateadmin/RefundCharge?patientUserID=${payload.patientUserID}&patientAffiliateID=${payload.patientAffiliateID}&questionnaireID=${payload.questionnaireID}&transactionID=${payload.transactionID}&refundAmount=${payload.refundAmount}&paymentTransactionType=${payload.paymentTransactionType}&refundNote=${payload.refundNote}`;
        const pVM = await api.post(postUrl);
        return pVM.data as AjaxReturnViewModel<boolean>;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    async UpdateCreditCard({ commit }, authToken) {
      try {
        const pVM = await api.post(`/api/home/UpdateCreditCard?authToken=${authToken}`);
        return pVM.data as AjaxReturnViewModel<boolean>;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    async HoldQuestionnaire({ commit, state }, payload: { patientUserID: number; patientAffiliateID: number; questionnaireID: number; hold: boolean }): Promise<AjaxReturnViewModel<boolean> | null> {
      try {
        let postUrl = "";
        if (state.Role == "Support") postUrl = `/api/support/HoldQuestionnaire?patientUserID=${payload.patientUserID}&patientAffiliateID=${payload.patientAffiliateID}&questionnaireID=${payload.questionnaireID}&hold=${payload.hold}`;
        if (state.Role == "AffiliateAdmin") postUrl = `/api/affiliateadmin/HoldQuestionnaire?patientUserID=${payload.patientUserID}&patientAffiliateID=${payload.patientAffiliateID}&questionnaireID=${payload.questionnaireID}&hold=${payload.hold}`;
        const r = await api.post(postUrl);
        return r.data;
      } catch (e) {
        return null;
      }
    },
    async SendQuestionnaireDraft(
      { commit, state },
      payload: { affiliateQuestionnaireTypeID: number; draftName: string; questionnaireName: string; questionnaireDescription: string; questionsJSON: string; questionsJSONId?: number; status: string; propertiesJSON: string; questionnaireType: string; externalID: string; displayImagePath: string; displayProductRxID: string; displayPriceDenominator: string; affiliateID?: number }
    ): Promise<AjaxReturnViewModel<boolean> | null> {
      try {
        let postUrl = "";
        const bodyPayload: any = {
          affiliateQuestionnaireTypeID: payload.affiliateQuestionnaireTypeID,
          draftName: payload.draftName,
          questionsName: payload.questionnaireName,
          questionsDescription: payload.questionnaireDescription,
          questionsJSON: payload.questionsJSON,
          propertiesJSON: payload.propertiesJSON,
          questionnaireType: payload.questionnaireType,
          externalID: payload.externalID,
          displayImagePath: payload.displayImagePath,
          displayProductRxID: payload.displayProductRxID,
          displayPriceDenominator: payload.displayPriceDenominator,
        };
        if (payload.questionsJSONId) {
          bodyPayload.QuestionsJSONId = payload.questionsJSONId;
        }
        if (payload.status) {
          bodyPayload.status = payload.status;
        }
        if (payload.affiliateID) {
          bodyPayload.affiliateID = payload.affiliateID;
        }

        if (state.Role == "AffiliateAdmin") postUrl = `/api/affiliateadmin/SendQuestionsDraftJSON`;
        if (state.Role == "Admin") postUrl = `/api/Admin/SendQuestionsDraftJSON`;
        const r = await api.post(postUrl, bodyPayload);
        return r.data;
      } catch (e) {
        return null;
      }
    },
    async PreviewBulkMessage({ commit, state }, payload: { selectionType: string; customerType: string; questionnaireTypeId?: string; productRxId?: string; userIds?: string; message?: string; affiliateId?: number }): Promise<AjaxReturnViewModel<boolean> | null> {
      try {
        let postUrl = "";
        const bodyPayload: any = {
          selectionType: payload.selectionType,
          message: payload.message,
          affiliateId: payload.affiliateId,
        };
        if (payload.userIds) {
          bodyPayload.userIds = payload.userIds;
        }
        if (payload.questionnaireTypeId) {
          bodyPayload.questionnaireTypeId = payload.questionnaireTypeId;
        }
        if (payload.productRxId) {
          bodyPayload.productRxId = payload.productRxId;
        }
        if (payload.customerType) {
          bodyPayload.customerType = payload.customerType;
        }

        if (state.Role == "Support") postUrl = `/api/support/PreviewBulkMessage`;
        const r = await api.post(postUrl, bodyPayload);
        return r.data;
      } catch (e) {
        return null;
      }
    },

    async SendBulkMessage({ commit, state }, payload: { selectionType: string; customerType: string; message: string; affiliateId: number; questionnaireTypeId: number; productRxId: number; userIds: string; attachment: string; attachmentType: number; folderType: number; rxId: number }) {
      try {
        const bodyPayload = {
          selectionType: payload.selectionType,
          customerType: payload.customerType,
          message: payload.message,
          affiliateId: payload.affiliateId,
          questionnaireTypeId: payload.questionnaireTypeId,
          productRxId: payload.productRxId,
          userIds: payload.userIds,
          attachment: payload.attachment,
          attachmentType: payload.attachmentType,
          folderType: payload.folderType,
          rxId: payload.rxId,
        };

        const result = await api.post(`/api/support/BulkMessages`, bodyPayload);
        if (!result || !result.data) {
          return { success: false, errors: ["Network error or invalid response"] };
        }
        return result.data;
      } catch (e) {
        console.log(e);
        return { success: false, errors: ["An unexpected error occurred"] };
      }
    },

    async DeleteDraftJson({ commit, state }, payload: { questionsJSONId: number; status: string; affiliateID?: number }): Promise<AjaxReturnViewModel<boolean> | null> {
      try {
        let postUrl = "";
        const bodyPayload: any = {
          questionsJSONId: payload.questionsJSONId,
          status: payload.status,
        };

        if (payload.affiliateID) {
          bodyPayload.affiliateID = payload.affiliateID;
        }
        if (state.Role == "AffiliateAdmin") postUrl = `/api/affiliateadmin/DeleteDraftJson`;
        if (state.Role == "Admin") postUrl = `/api/Admin/DeleteDraftJson`;
        const r = await api.post(postUrl, bodyPayload);
        return r.data;
      } catch (e) {
        return null;
      }
    },
    async UpdateDraftJSONStatus(
      { commit, state },
      payload: { questionsJSONId: number; status: string; affiliateQuestionnaireTypeID: number; questionnaireName: string; questionnaireDescription: string; questionsJSON: string; propertiesJSON: string; questionnaireType: string; externalID: string; displayImagePath: string; displayProductRxID: string; displayPriceDenominator: string }
    ): Promise<AjaxReturnViewModel<boolean> | null> {
      try {
        let postUrl = "";

        const bodyPayload: any = {
          questionsJSONId: payload.questionsJSONId,
          status: payload.status,
          affiliateQuestionnaireTypeID: payload.affiliateQuestionnaireTypeID,
          questionnaireName: payload.questionnaireName,
          questionnaireDescription: payload.questionnaireDescription,
          questionsJSON: payload.questionsJSON,
          propertiesJSON: payload.propertiesJSON,
          questionnaireType: payload.questionnaireType,
          externalID: payload.externalID,
          displayImagePath: payload.displayImagePath,
          displayProductRxID: payload.displayProductRxID,
          displayPriceDenominator: payload.displayPriceDenominator,
        };

        if (state.Role == "AffiliateAdmin") postUrl = `/api/affiliateadmin/UpdateDraftJSONStatus`;
        if (state.Role == "Admin") postUrl = `/api/Admin/UpdateDraftJSONStatus`;
        const r = await api.post(postUrl, bodyPayload);
        return r.data;
      } catch (e) {
        return null;
      }
    },
    async getUserQuestionsJSONDrafts({ commit, state }, payload: { ID: number; draftName: string; questionnaireType: string; createdByUserId: number; affiliateID: number }): Promise<AjaxReturnViewModel<boolean> | null> {
      try {
        const bodyPayload: any = {
          affiliateQuestionnaireTypeID: payload.ID,
        };
        if (payload.draftName) {
          bodyPayload.draftName = payload.draftName;
        }
        if (payload.questionnaireType) {
          bodyPayload.questionnaireType = payload.questionnaireType;
        }
        if (payload.createdByUserId) {
          bodyPayload.createdByUserId = payload.createdByUserId;
        }

        if (payload.affiliateID) {
          bodyPayload.affiliateID = payload.affiliateID;
        }

        let postUrl = "";
        if (state.Role == "AffiliateAdmin") postUrl = `/api/affiliateadmin/UserQuestionsJSONDrafts`;
        if (state.Role == "Admin") postUrl = `/api/Admin/UserQuestionsJSONDrafts`;
        const r = await api.post(postUrl, bodyPayload);
        return r.data.data;
      } catch (e) {
        return null;
      }
    },
    async getAffiliateQuestionsJson({ commit, state }, payload: { affiliateId: number }): Promise<AjaxReturnViewModel<boolean> | null> {
      try {
        let postUrl = "";
        if (state.Role == "Admin") postUrl = `/api/Admin/UserQuestionsJSONDrafts?affiliateId=${payload.affiliateId}`;
        const r = await api.get(postUrl);
        return r.data.data;
      } catch (e) {
        return null;
      }
    },

    async SavePermission({ commit, state }, payload: { code: string; description?: string; active?: boolean }): Promise<AjaxReturnViewModel<any> | null> {
      try {
        const postUrl = state.Role === "AffiliateAdmin" ? `/api/AffiliateAdmin/CreatePermission` : `/api/Admin/CreatePermission`;
        const bodyPayload: any = {
          Code: payload.code?.trim(),
          Description: payload.description ?? null,
          Active: typeof payload.active === "boolean" ? payload.active : true,
        };

        const r = await api.post(postUrl, bodyPayload);
        return r.data;
      } catch (e) {
        return null;
      }
    },
    async UpsertRolePermission({ commit, state }, payload: { id: number; roleID: number; permissionID: number; affiliateID: number | null; granted: boolean; active: boolean }): Promise<AjaxReturnViewModel<any> | null> {
      try {
        const postUrl = state.Role === "AffiliateAdmin" ? `/api/AffiliateAdmin/UpsertRolePermissions` : `/api/Admin/UpsertRolePermissions`;
        const bodyPayload: any = {
          ID: payload.id,
          RoleID: payload.roleID,
          PermissionID: payload.permissionID,
          AffiliateID: payload.affiliateID,
          Granted: payload.granted,
          Active: payload.active,
        };

        const r = await api.post(postUrl, bodyPayload);
        return r.data;
      } catch (e) {
        return null;
      }
    },
    async AllPermissions({ commit, state }): Promise<AjaxReturnViewModel<any> | null> {
      try {
        const postUrl = state.Role === "AffiliateAdmin" ? `/api/AffiliateAdmin/Permissions` : `/api/Admin/Permissions`;
        const r = await api.get(postUrl);
        return r.data;
      } catch (e) {
        return null;
      }
    },
    async AllRolePermissions({ commit, state }): Promise<AjaxReturnViewModel<any> | null> {
      try {
        const postUrl = state.Role === "AffiliateAdmin" ? `/api/AffiliateAdmin/AllRolePermissions` : `/api/Admin/AllRolePermissions`;
        const r = await api.get(postUrl);
        return r.data;
      } catch (e) {
        return null;
      }
    },
    async UserPermissions({ commit, state }, payload: { userID: number; active: boolean }): Promise<AjaxReturnViewModel<any> | null> {
      try {
        const getUrl = state.Role === "AffiliateAdmin" ? "/api/AffiliateAdmin/UserPermissions" : "/api/Admin/UserPermissions";

        const r = await api.get(getUrl, {
          params: {
            UserID: payload.userID,
            Active: payload.active,
          },
        });

        return r.data;
      } catch (e) {
        return null;
      }
    },
    async AllUserPermissions({ commit, state }): Promise<AjaxReturnViewModel<any> | null> {
      try {
        const postUrl = state.Role === "AffiliateAdmin" ? `/api/AffiliateAdmin/AllUserPermissions` : `/api/Admin/AllUserPermissions`;
        const r = await api.get(postUrl);
        return r.data;
      } catch (e) {
        return null;
      }
    },
    async UpsertUserPermission({ commit, state }, payload: { id?: number; userID: number; permissionId: number; affiliateId: number; granted: boolean; active: boolean }): Promise<AjaxReturnViewModel<any> | null> {
      try {
        const postUrl = state.Role === "AffiliateAdmin" ? `/api/AffiliateAdmin/UpsertUserPermissions` : `/api/Admin/UpsertUserPermissions`;
        const bodyPayload: any = {
          ID: payload.id,
          UserID: payload.userID,
          PermissionID: payload.permissionId,
          AffiliateID: payload.affiliateId,
          Granted: payload.granted,
          Active: payload.active,
        };

        const r = await api.post(postUrl, bodyPayload);
        return r.data;
      } catch (e) {
        return null;
      }
    },
    async DeleteUserPermission({ commit, state }, payload: { id?: number; userID: number; permissionId: number; affiliateId: number }): Promise<AjaxReturnViewModel<any> | null> {
      try {
        const postUrl = state.Role === "AffiliateAdmin" ? `/api/AffiliateAdmin/DeleteUserPermission` : `/api/Admin/DeleteUserPermission`;
        const bodyPayload: any = {
          ID: payload.id,
          UserID: payload.userID,
          PermissionID: payload.permissionId,
          AffiliateID: payload.affiliateId,
        };

        const r = await api.post(postUrl, bodyPayload);
        return r.data;
      } catch (e) {
        return null;
      }
    },
    async DeleteRolePermission({ commit, state }, payload: { id?: number; userID: number; permissionId: number; affiliateId: number }): Promise<AjaxReturnViewModel<any> | null> {
      try {
        const postUrl = state.Role === "AffiliateAdmin" ? `/api/AffiliateAdmin/DeleteRolePermission` : `/api/Admin/DeleteRolePermission`;
        const bodyPayload: any = {
          ID: payload.id,
          UserID: payload.userID,
          PermissionID: payload.permissionId,
          AffiliateID: payload.affiliateId,
        };

        const r = await api.post(postUrl, bodyPayload);
        return r.data;
      } catch (e) {
        return null;
      }
    },
    async GetAffiliatesForReportManagement({ commit }) {
      try {
        const r = await api.get(`/api/reportmanagement/GetAffiliatesForReportManagement`);
        return r.data.data;
      } catch (e) {
        return null;
      }
    },
    async GetSprocList({ commit }) {
      try {
        const r = await api.get(`/api/reportmanagement/GetSprocList`);
        return r.data.data;
      } catch (e) {
        return null;
      }
    },
    async CreateReport({ commit }, payload: OutgoingReport): Promise<AjaxReturnViewModel<boolean> | null> {
      try {
        const r = await api.post(`/api/reportmanagement/CreateReport`, payload);
        return r.data;
      } catch (e) {
        return null;
      }
    },
    async GetReportsForManagement({ commit }) {
      try {
        const r = await api.get(`/api/reportmanagement/GetReportsForManagement`);
        return r.data;
      } catch (e) {
        return null;
      }
    },
    async UpdateReport({ commit }, payload: ReportDataManagementViewModel): Promise<AjaxReturnViewModel<boolean> | null> {
      try {
        const r = await api.post(`/api/reportmanagement/UpdateReport`, payload);
        return r.data;
      } catch (e) {
        return null;
      }
    },
    async DeleteReport({ commit }, payload: number): Promise<AjaxReturnViewModel<boolean> | null> {
      try {
        console.log(`payload id: ${payload}`);
        const r = await api.post(`/api/reportmanagement/DeleteReport/${payload}`);
        return r.data;
      } catch (e) {
        return null;
      }
    },
    async GetAllUsersForReportManagement({ commit }) {
      try {
        const r = await api.get(`/api/reportmanagement/GetAllUsersForReportManagement`);
        return r.data;
      } catch (e) {
        return null;
      }
    },
    // async GetMaintenanceStatus({ commit }) {
    //   try {
    //     const r = await api.get(`/api/maintenance/GetMaintenanceStatus`);
    //     commit("setMaintenanceDetails", r.data.data);
    //     return r.data;
    //   } catch (e) {
    //     return null;
    //   }
    // },
    // async PostMaintenance({ commit }, payload: any) {
    //   try {
    //     const r = await api.post(`/api/maintenance/PostMaintenance`, payload);
    //     return r.data.success;
    //   } catch (e) {
    //     return null;
    //   }
    // },
    // async SetAffiliateMaintenanceModeContent({ commit }, payload: any) {
    //   try {
    //     const r = await api.post(`/api/maintenance/SetAffiliateMaintenanceModeContent_${payload.useDefault}`, JSON.stringify(payload.html), {
    //       headers: {
    //         "Content-Type": "application/json",
    //       },
    //     });
    //     return r.data.success;
    //   } catch (e) {
    //     return null;
    //   }
    // },
    // async GetAllMaintenance({ commit }) {
    //   try {
    //     const r = await api.get(`/api/maintenance/GetAllMaintenance`);
    //     return r.data;
    //   } catch (e) {
    //     return null;
    //   }
    // },
    // async GetAffiliatesForMaintenanceAssignation({ commit }) {
    //   try {
    //     const r = await api.get(`/api/maintenance/GetAffiliatesForMaintenanceAssignation`);
    //     return r.data;
    //   } catch (e) {
    //     return null;
    //   }
    // },
    // async GetDefaultMaintenanceHTML({ commit }) {
    //   try {
    //     const r = await api.get(`/api/maintenance/GetDefaultMaintenanceHTML`);
    //     return r.data.data;
    //   } catch (e) {
    //     return null;
    //   }
    // },
    // async DeleteMaintenance({ commit }, payload: number) {
    //   try {
    //     const r = await api.post(`/api/maintenance/DeleteMaintenance_${payload}`);
    //     return r.data;
    //   } catch (e) {
    //     return null;
    //   }
    // },
    // async GetMaintenanceIntervals({ commit }) {
    //   try {
    //     const r = await api.get("/api/maintenance/GetMaintenanceIntervals");
    //     if (r && r.data && r.data.data && r.data.data.notWithinMaintenanceWindow && r.data.data.withinMaintenanceWindow) {
    //       commit("setNotWithinMaintenanceWindow", r.data.data.notWithinMaintenanceWindow);
    //       commit("setWithinMaintenanceWindow", r.data.data.withinMaintenanceWindow);
    //     }
    //   } catch (e) {
    //     console.log(e);
    //   }
    // },
    // async GetAllCallToActions({ commit }, payload: boolean) {
    //   // If true, then it's an admin user and we're getting all to display in the table.
    //   try {
    //     const r = await api.get(`/api/calltoaction/all/${payload}`);
    //     commit("setCallToActionArray", r.data.data);
    //     return r.data.data;
    //   } catch (e) {
    //     return null;
    //   }
    // },
    // async GetCallToActionByID({ commit }, ctaID: number) {
    //   try {
    //     const r = await api.get(`/api/calltoaction/details/${ctaID}`);
    //     return r.data.data;
    //   } catch (e) {
    //     return null;
    //   }
    // },
    // async PostCallToAction({ commit }, payload: any) {
    //   let errorMessage = "";
    //   try {
    //     if (payload.id) {
    //       const r = await api.post(`/api/calltoaction/update`, payload);
    //       errorMessage = r.data.errors;
    //       return r.data.success;
    //     } else {
    //       const r = await api.post(`/api/calltoaction/new`, payload);
    //       errorMessage = r.data.errors;
    //       this.commit("setCallToActionArray", []);
    //       return r.data.success;
    //     }
    //   } catch (e) {
    //     return errorMessage;
    //   }
    // },
    async GetWeightTrackerEntries({ commit }) {
      try {
        const result = await api.get("api/home/GetWeightTrackerEntries");
        return result.data.data;
      } catch (e) {
        return null;
      }
    },
    async GetFullPatientWidgetData({ commit }) {
      try {
        const result = await api.get("api/home/GetFullPatientWidgetData");
        commit("setFullPatientWidgetData", result.data.data);
        return result.data.data;
      } catch (e) {
        return null;
      }
    },
    async GetGoalWeight({ commit }) {
      try {
        const result = await api.get("api/home/GetGoalWeight");
        return result.data.data;
      } catch (e) {
        return null;
      }
    },
    async SetGoalWeight({ commit }, payload: any) {
      try {
        const result = await api.post("api/home/SetGoalWeight", payload);
        return result.data.success;
      } catch (e) {
        return null;
      }
    },
    async AddWeightLogEntry({ commit }, payload: any) {
      try {
        const result = await api.post("api/home/AddWeightLogEntry", payload);
        return result.data.success;
      } catch (e) {
        return null;
      }
    },
    async GetPatientVitals({ commit }, payload: PatientVitalViewModel) {
      try {
        console.log(payload);
        const resp = await api.post("/api/physician/GetPatientVitals", payload);
        if (resp && resp.data && resp.data.success) {
          return resp.data as AjaxReturnViewModel<PatientVitalViewModel[]>;
        }
      } catch (e) {
        console.log(e);
      }
    },
    async DisableTwoFactor({ commit, state }, payload: { patientUserID: number; patientAffiliateID: number }) {
      try {
        let postUrl = "";
        if (state.Role == "Support") postUrl = `/api/support/DisableTwoFactor?patientUserID=${payload.patientUserID}&patientAffiliateID=${payload.patientAffiliateID}`;
        if (state.Role == "AffiliateAdmin") postUrl = `/api/affiliateadmin/DisableTwoFactor?patientUserID=${payload.patientUserID}&patientAffiliateID=${payload.patientAffiliateID}`;
        if (state.Role == "Pharmacy") postUrl = `/api/pharmacy/DisableTwoFactor?patientUserID=${payload.patientUserID}&patientAffiliateID=${payload.patientAffiliateID}`;
        const r = await api.post(postUrl);
        return r.data;
      } catch (e) {
        return null;
      }
    },
    async GetAffiliateUsersForPromotions({ commit }) {
      try {
        const resp = await api.get("/api/affiliateadmin/GetAffiliateUsersForPromotions");
        return resp.data.data;
      } catch (error) {
        return error;
      }
    },
    async Paged_GetAffiliateUsersForPromotions({ commit }, QueryParams: string) {
      try {
        const resp = await api.get(`/api/affiliateadmin/Paged_GetAffiliateUsersForPromotions${QueryParams}`);
        return resp.data.data;
      } catch (error) {
        return error;
      }
    },
    async Search_GetAffiliateUsersForPromotions({ commit }, MatchQuery: string) {
      try {
        const resp = await api.get(`/api/affiliateadmin/Search_GetAffiliateUsersForPromotions${MatchQuery}`);
        return resp.data.data;
      } catch (error) {
        return error;
      }
    },
    async Paged_GetAffiliateUsersForPromotionsAdmin({ commit }, QueryParams: string) {
      try {
        const resp = await api.get(`/api/admin/Paged_GetAffiliateUsersForPromotions${QueryParams}`);
        return resp.data.data;
      } catch (error) {
        return error;
      }
    },
    async Search_GetAffiliateUsersForPromotionsAdmin({ commit }, MatchQuery: string) {
      try {
        const resp = await api.get(`/api/admin/Search_GetAffiliateUsersForPromotions${MatchQuery}`);
        return resp.data.data;
      } catch (error) {
        return error;
      }
    },
    async GetPromotionUsersByPromotionID({ commit }, payload: any) {
      let url = "/api/affiliateadmin/GetPromotionUsersByPromotionID/";
      if (payload.isAdmin) url = "/api/admin/GetPromotionUsersByPromotionID/";
      try {
        const resp = await api.get(url + payload.id);
        return resp.data.data;
      } catch (error) {
        return error;
      }
    },
    async SaveReschedule({ commit, state }, payload: any) {
      try {
        let url = `/api/home/SaveReschedule?event_id=${payload}`;
        if (state.Role == "Support") url = `/api/support/SaveReschedule?patientUserID=${payload.patientUserID}&patientAffiliateID=${payload.patientAffiliateID}`;
        const resp = await api.get(url);
        return resp.data.data;
      } catch (error) {
        return error;
      }
    },
    async UpdateDeviceTouch({ commit }) {
      try {
        const response = await api.post("/api/home/UpdateDeviceTouch");

        return response.data;
      } catch (error) {
        console.error("Error in UpdateDeviceTouch:", error);
        return null;
      }
    },
    async QualiphyInviteResend({ commit }, questionnaireTypeID: number) {
      try {
        const response = await api.post("/api/home/QualiphyInviteResend?questionnaireTypeID=" + questionnaireTypeID);
        return response.data;
      } catch (error) {
        console.error("Error in QualiphyInviteResend:", error);
        return null;
      }
    },
    async GetSMSCode({ commit, state }, payload: { phone: string }): Promise<AjaxReturnViewModel<boolean> | null> {
      try {
        const resp = await api.post("/api/members/GetSMSCode", payload);
        if (resp) {
          return resp.data;
        }
      } catch (e) {
        console.log(e);
      }
      return null;
    },
    async ValidateSMSCode({ commit, state }, payload: { code: string }): Promise<AjaxReturnViewModel<boolean> | null> {
      try {
        const resp = await api.post("/api/members/ValidateSMSCode", payload);
        if (resp) {
          return resp.data;
        }
      } catch (e) {
        console.log(e);
      }
      return null;
    },
    async ToggleSMS({ commit }, payload: { enabled: boolean }): Promise<AjaxReturnViewModel<boolean> | null> {
      try {
        const resp = await api.post("/api/members/ToggleSMS", payload);
        if (resp && resp.data) {
          return resp.data;
        }
      } catch (e) {
        console.log("Error in ToggleSMS action:", e);
      }
      return null;
    },
    async ResetPhoneNumberConfirmed({ commit, state }) {
      try {
        const pVM = await api.post("/api/members/ResetPhoneNumberConfirmed");
        return pVM.data as AjaxReturnViewModel<boolean>;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    async CreatequickLink({ commit }, dto: any) {
      try {
        return await api.post("/api/support/CreatequickLink", dto);
      } catch (e) {
        console.error(`Error due to ${e}`);
        return null;
      }
    },
    async GetQuickLinkMasquerade({ commit }) {
      try {
        if (this.state.QuickLinkExternalID) {
          const response = await api.get(`/api/Home/GetQuickLinkMasquerade/${this.state.QuickLinkExternalID}`);
          commit("setQuickLinkSupportUserName", response.data.data);
          return response.data.data;
        } else {
          return null;
        }
      } catch (error) {
        console.error(`Error due to ${error}`);
        return null;
      }
    },
    async quickLinkAuthentication({ commit }, objQL: any) {
      try {
        commit("setLoading", true);
        const response = await api.get(`/api/members/quickLinkAuthentication?ID=${objQL.id}&t=${objQL.t}`);
        if (response.status === 200 && response.data && response.data.auth && response.data.ql) {
          commit("setToken", response.data.auth.token);
          commit("setRefreshToken", response.data.auth.refreshToken);
          commit("setRole", response.data.auth.role);
          commit("setPhotoPath", response.data.auth.photoPath);
          commit("setFirstName", response.data.auth.firstName);
          commit("setLastName", response.data.auth.lastName);
          commit("setEmail", response.data.email);
          commit("setIsVerifiedQuickLink", true);
          if (response.data.ql.questionnaireTypeID) commit("setCurrentQuestType", response.data.ql.questionnaireTypeID);
          commit("setIsQuickLinkUser", true);
          if (response.data.ql.destinationTypeID == 5) {
            commit("setIsQuickLinkSupportUser", true);
          }
          this.state.SignedIn = true;
          commit("setLoading", false);
          return response.data.ql.destinationTypeID;
        } else {
          return 100;
        }
      } catch (e) {
        console.error(`Error due to ${e}`);
        return null;
      }
    },
    async GetAllquickLinksByTargetUserID({ commit }, payload: { targetUserID: number; affiliateID: number }) {
      try {
        const response = await api.get(`/api/support/GetAllquickLinksByTargetUserID/${payload.targetUserID}?affiliateID=${payload.affiliateID}`);
        return JSON.stringify(response.data.data);
      } catch (e) {
        console.error(`Error due to ${e}`);
        return null;
      }
    },
    async GetQuestionnaireStatusForQuickLinkPayment({ commit }, payload: { targetUserID: number; affiliateID: number }) {
      try {
        const response = await api.get(`/api/support/GetQuestionnaireStatusForQuickLinkPayment/${payload.targetUserID}?affiliateID=${payload.affiliateID}`);
        return response.data.data;
      } catch (e) {
        console.error(`Error due to ${e}`);
        return [];
      }
    },

    async GetPharmacyMedicineSearchTerm({ commit, state }, { query }) {
      const postUrl = "/api/medicationmarketplace/GetPharmacyMedicineSearchTerm/";

      try {
        const resp = await api.post(postUrl, null, {
          params: { query },
        });
        if (resp && resp.data && resp.data.success) {
          return resp.data as AjaxReturnViewModel<string[]>;
        }
      } catch (e) {
        console.log(e);
      }
    },
    async GetPharmacyMedicineSearchTermResults({ commit, state }, { query }) {
      const postUrl = "/api/medicationmarketplace/GetPharmacyMedicineSearchTermResults/";

      try {
        const resp = await api.post(postUrl, null, {
          params: { query },
        });
        if (resp && resp.data && resp.data.success) {
          return resp.data as AjaxReturnViewModel<MedicationMarketplaceDrugListViewModel>;
        }
      } catch (e) {
        console.log(e);
      }
    },

    async UpdateMarketplaceEstimates({ commit }, vm: MarketplaceEstimatesViewModel[]) {
      try {
        const pVM = await api.post("/api/medicationmarketplace/UpdateMarketplaceEstimates", vm);
        return pVM.data as AjaxReturnViewModel<boolean>;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    async PharmacyGetDrugConfigs({ commit }, WrapperObj: any) {
      try {
        const result = await api.get(`/api/pharmacy/GetDrugConfigs/${WrapperObj.status}${WrapperObj.qp}`);
        if (result && result.data) {
          return result.data;
        } else {
          return null;
        }
      } catch (e) {
        console.error(`Error due to ${e}`);
        return null;
      }
    },
    async PharmacyUpdateDrugConfig({ commit }, Wrapper: any) {
      try {
        const result = await api.post(`/api/pharmacy/UpdateDrugConfig/${Wrapper.Status}${Wrapper.QueryParams}`, Wrapper.DrugConfig);
        if (result && result.data) {
          return result.data;
        } else {
          return null;
        }
      } catch (e) {
        console.error(`Error due to ${e}`);
        return null;
      }
    },
    async PharmacyGetDrugConfigQtyByDrugID({ commit }, drugConfigID) {
      try {
        const result = await api.get(`/api/Pharmacy/GetDrugConfigQtyByDrugID/${drugConfigID}`);
        if (result && result.data) {
          return result.data;
        } else {
          return null;
        }
      } catch (error) {
        console.log(error);
      }
    },
    async PharmacyGetAffiliatesForPharmacyUser({ commit }) {
      try {
        const result = await api.get(`/api/Pharmacy/GetAffiliatesForPharmacyUser`);
        if (result && result.data) {
          return result.data;
        } else {
          return null;
        }
      } catch (error) {
        console.log(error);
      }
    },
    async PharmacyGetAffiliateDrugsByAffiliateID({ commit }, Wrapper: any) {
      try {
        const result = await api.get(`/api/Pharmacy/GetAffiliateDrugsByAffiliateID/${Wrapper.affID}${Wrapper.query}`);
        if (result && result.data) {
          return result.data;
        } else {
          return null;
        }
      } catch (error) {
        console.log(error);
      }
    },
    async PharmacyAddSelectedToAffiliateCatalog({ commit }, WrapperObj: any) {
      try {
        const result = await api.post(`/api/Pharmacy/AddSelectedToAffiliateCatalog/${WrapperObj.sourceAffiliateID}/${WrapperObj.targetAffiliateID}/${WrapperObj.isCopy}`, WrapperObj.IncomingList);
        if (result && result.data && result.data.success) {
          return result.data.success;
        } else {
          return false;
        }
      } catch (error) {
        console.log(error);
      }
    },
    async PharmacyRemoveAffiliateCatalogDrugByID({ commit }, AffiliateDrugID: any) {
      try {
        const result = await api.post(`/api/Pharmacy/RemoveAffiliateCatalogDrugByID/${AffiliateDrugID}`);
        if (result && result.data.success) {
          return result.data.success;
        } else {
          return false;
        }
      } catch (error) {
        console.log(error);
      }
    },
    async PharmacyGetUnselectedCatalog({ commit }, AffWrapperObj: any) {
      /*
        AffWrapperObj = {
          query: this.outgoingQueryParams,
          affID: this.selectedAffiliate.id
        };
      */
      try {
        const result = await api.get(`/api/pharmacy/GetUnselectedCatalog/2/${AffWrapperObj.affID}${AffWrapperObj.query}`);
        if (result && result.data) {
          return result.data;
        } else {
          return null;
        }
      } catch (e) {
        console.error(`Error due to ${e}`);
        return null;
      }
    },

    async GetAffiliateSubscriptionData({ commit, state }) {
      try {
        const resp = await api.get("/api/medicationmarketplace/GetAffiliateSubscriptionData");
        if (resp && resp.data) {
          return resp.data;
        }
      } catch (e) {
        console.log(e);
      }
      return null;
    },

    async GetPatientData({ commit, state }) {
      try {
        const result = await api.get(`/api/medicationmarketplace/GetPatientData`);
        if (!result || !result.data || !result.data.success) return null;
        return result.data.data;
      } catch (e) {
        console.log(e);
        return null;
      }
    },

    async TransferMedication({ commit }, vm: SubscriptionTransferViewModel) {
      try {
        const pVM = await api.post("/api/medicationmarketplace/TransferMedication", vm);
        return pVM.data as AjaxReturnViewModel<boolean>;
      } catch (e) {
        console.log(e);
        return null;
      }
    },

    async GetMedicationDetails({ commit }, { rxID, associatedUserID }: { rxID: number; associatedUserID?: number | null }) {
      try {
        const params = new URLSearchParams();
        params.append("rxID", rxID.toString());
        if (associatedUserID != null) {
          params.append("associatedUserID", associatedUserID.toString());
        }

        const response = await api.get(`/api/medicationmarketplace/GetMedicationDetails?${params.toString()}`);
        return response.data;
      } catch (e) {
        console.error("Error fetching medication details:", e);
        return null;
      }
    },
    async GetFamilyPlanDetails({ commit }) {
      try {
        const response = await api.get(`/api/medicationmarketplace/GetFamilyPlanDetails`);
        return response.data;
      } catch (e) {
        console.error("Error fetching famly plan details:", e);
        return null;
      }
    },

    async UpdateFamilyPlanMember({ commit }, member: FamilyPlanManagerViewModel) {
      try {
        const response = await api.post("/api/medicationmarketplace/UpdateFamilyPlanMember", member);

        if (response.data) {
          return response.data;
        }
      } catch (e) {
        console.log("Error updating family plan member:", e);
        return false;
      }
    },

    async GetPendingFamilyPlanRequest({ commit }) {
      try {
        const response = await api.get(`/api/medicationmarketplace/GetPendingFamilyPlanRequest`);
        return response.data;
      } catch (e) {
        console.error("Error fetching famly plan invite:", e);
        return null;
      }
    },

    async FamilyPlanInvitationConfirmation({ commit }, vm) {
      try {
        const response = await api.post("/api/medicationmarketplace/FamilyPlanInvitationConfirmation", vm);
        return response;
      } catch (e) {
        console.error("Error updating famly plan invite:", e);
        return null;
      }
    },

    async UpdatePreferredLanguage({ commit }, lang: string) {
      try {
        const pVM = await api.post("/api/members/UpdatePreferredLanguage?lang=" + encodeURIComponent(lang));
        return pVM.data as AjaxReturnViewModel<boolean>;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    async GetPreferredLanguage({ commit, state }) {
      try {
        const resp = await api.get("/api/members/GetPreferredLanguage");
        if (resp && resp.data) {
          return resp.data;
        }
      } catch (e) {
        console.log(e);
      }
      return null;
    },

    async GetMarketplacePaymentHistory({ commit }) {
      try {
        const response = await api.get(`/api/medicationmarketplace/GetMarketplacePaymentHistory`);
        return response.data;
      } catch (e) {
        console.error("Error fetching marketplace payment history:", e);
        return null;
      }
    },

    async GetAffiliateSubscriptionAdminData({ commit, state }, affiliateID?) {
      try {
        let getUrl = "";
        if (state.Role == "Admin") getUrl = `/api/admin/GetAffiliateSubscriptionAdminData/${affiliateID}`;
        if (state.Role == "AffiliateAdmin") getUrl = `/api/affiliateadmin/GetAffiliateSubscriptionAdminData`;

        const resp = await api.get(getUrl);
        return resp?.data ?? null;
      } catch (e) {
        console.log(e);
      }
      return null;
    },

    async UpdateAffiliateSubscriptions({ commit, state }, vm: UpdateAffiliateSubscriptionsViewModel) {
      try {
        let postUrl = "";
        if (state.Role == "Admin") postUrl = `/api/admin/UpdateAffiliateSubscriptions`;
        if (state.Role == "AffiliateAdmin") postUrl = `/api/affiliateadmin/UpdateAffiliateSubscriptions`;

        const pVM = await api.post(postUrl, vm);
        return pVM.data as AjaxReturnViewModel<boolean>;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
  },
  modules: {
    quiz: quizModule,
  },
  plugins: [
    createPersistedState({
      key: "quiz-app", // A unique key for the persisted state
      paths: ["quiz.activeQuizzes"], // Only persist the activeQuizzes part of the quiz module
    }),
  ],
});
